/**
 * AudioDecoder — robust three-tier decode pipeline for user-supplied audio.
 *
 * Single source of truth for "turn this File / Blob / ArrayBuffer into an
 * AudioBuffer for tool UI" across the project. Replaces the scattered
 * try/catch+toast pattern that lived in ~30 tool blades and missed two
 * recovery layers, surfacing as the 2026-04-29 user complaint:
 * /ru/volume on Yandex Browser silently rejected an OGG and the user
 * reported "не подгружает файл .wav" with last_error: null.
 *
 * Pipeline:
 *
 *   Tier 1 — native AudioContext.decodeAudioData
 *     Hardware-accelerated, fastest, zero memory above the source ArrayBuffer.
 *     Wrapped in a 30-second timeout so a hung Yandex / older-Android decode
 *     doesn't stall forever (some Vorbis/Opus OGGs and edge WAV layouts).
 *
 *   Tier 2 — FFmpeg WASM PCM-s16le passthrough
 *     For files Tier 1 rejects (EncodingError, NotSupportedError) or times
 *     out. Decode + re-encode to a clean 16-bit stereo 44.1 kHz WAV via the
 *     shared FFmpegProcessor, then feed that to decodeAudioData. Lossy on
 *     32-bit-float source, but volume / loudness analysis tolerates it.
 *
 *   Tier 3 — AudioRecover (browser-decode-rescue)
 *     For files Tier 2 *also* rejects — typically broken containers
 *     (moov-missing M4A, truncated MP4, mis-named ext). AudioRecover uses
 *     the browser's own media stack with a different MIME hint and a
 *     stricter byte-level read, often succeeding where decodeAudioData of
 *     the raw upload didn't. Returns a clean PCM WAV blob we re-decode.
 *
 * Telemetry: every tier failure emits __aeToolError({error_type:'decode_tier_failed',
 * tier, ext, type, size, br}); the final-failure path additionally writes
 * window.__LAST_TOOL_ERROR so feedback-widget snapshots from real users
 * carry the actual error chain instead of last_error: null. The success
 * path emits __ae('feature_use', {f:'audio_decode_recovered', tier, ms})
 * for tier 2/3 wins so we can watch how often each rescue layer fires.
 *
 *   var { buffer, tier, ms } = await window.AudioDecoderRobust.robust(file, ctx, {
 *       slug: 'volume',          // for telemetry
 *       onProgress: pct => …,    // 0..1, fires across tiers
 *       signal: abortSignal,     // AbortController support
 *       browserOnly: true,       // decoration / second opinion: native + WebCodecs
 *                                // only — never the shared FFmpeg engine or the
 *                                // AudioRecover tier (see the block before Tier 2)
 *   });
 *
 * No ctx? We allocate one and keep it alive across tiers (Tier 2's WAV
 * re-decode reuses it). Cap on Tier 2 input size: deviceMemory × 60 MB
 * clamped to [80, 800] MB to mirror __effectiveFileLimit('audio'). Above
 * that, throw the original Tier-1 error rather than OOM the tab.
 *
 * Backwards compatibility: window.__decodeAudio(ctx, file) is preserved as
 * a thin wrapper so the 9 tools that already use it keep working with no
 * change. New code should use AudioDecoderRobust.robust directly.
 */
(function() {
'use strict';

// Tier-1 timeout is ADAPTIVE since 2026-07-08 (comment #938): the fixed
// 30 s killed decodes of long files on slow CPUs — a 90-min audiobook m4a
// needs 40-90 s of decodeAudioData on a 4-core box while the decode is
// perfectly healthy. decodeAudioData runs on Chromium's media thread pool
// (CDP CPU-throttling can't even reach it), and its wall time scales with
// SAMPLE COUNT ≈ duration. Duration is unknown pre-decode, but file SIZE is
// known instantly and correlates via bitrate (62 MB @96 kbps ≈ 90 min).
// timeout = 30 s + 1.5 s/MB, capped at 300 s:
//   3 MB podcast  → 34.5 s  (≈ legacy behaviour)
//   62 MB book    → 123 s   (the #938 file — was killed at 30 s)
//   388 MB rip    → 300 s   (cap)
// A genuinely wedged decode still settles in bounded time; a slow-but-alive
// one is no longer executed. Overridable for tests via window.__tier1BaseMs
// / window.__tier1PerMbMs.
var TIER_1_BASE_MS = 30000;
var TIER_1_PER_MB_MS = 1500;
var TIER_1_CAP_MS = 300000;
function tier1TimeoutMs(file) {
    var base = (typeof window !== 'undefined' && (window.__tier1BaseMs | 0)) || TIER_1_BASE_MS;
    var perMb = (typeof window !== 'undefined' && window.__tier1PerMbMs != null)
        ? (window.__tier1PerMbMs | 0) : TIER_1_PER_MB_MS;
    var sizeMb = Math.max(0, ((file && file.size) || 0) / 1048576);
    return Math.min(TIER_1_CAP_MS, Math.round(base + sizeMb * perMb));
}
// Hard upper bounds for the two long-pole tiers whose awaits were otherwise
// STRUCTURALLY UNBOUNDED — so robust() is guaranteed to settle in finite time
// for ALL ~32 callers (incl. the 30 tools that call robust() directly and have
// no watchdog of their own), closing the silent-hang class at its source rather
// than only at the uploader boundary. Bounds are generous (well above the
// codebase's accepted legit durations: WebCodecs mp3 = "seconds", FFmpeg WASM
// decode = 2-3 min per ffmpeg-processor.js) so a normal slow decode never trips
// them — they fire ONLY on a genuinely wedged tier. (setTimeout-based, so on an
// iOS-backgrounded tab they pause and fire on return-to-foreground, which is the
// acceptable degradation: the user isn't staring at a frozen tool meanwhile.)
// Audit: workflow wf_1729e989 2026-06-20. See reference_decode_integration_watchdog.
// Tier-1.5 outer bound. Since 2026-07-08 the mp4-family WebCodecs path has
// its own PROGRESS watchdog (60 s of no decoded frames → fail, re-armed on
// every frame — webcodecs-audio-decode.js), so stalls are caught there; this
// outer limit is only the catastrophic backstop and scales with file size
// like tier-1 (prod case #938: 43 MB mp3 legitimately needed 83.7 s; a
// 90-min m4a on 4 cores needs more than the old fixed 120 s).
var TIER_15_TIMEOUT_MS = (typeof window !== 'undefined' && (window.__tier15TimeoutMs | 0)) || 120000;       // floor; see tier15TimeoutMs()
function tier15TimeoutMs(file) {
    return Math.max(TIER_15_TIMEOUT_MS, tier1TimeoutMs(file) * 2);
}
var TIER_2_PROCESS_TIMEOUT_MS = (typeof window !== 'undefined' && (window.__tier2ProcMs | 0)) || 300000; // FFmpeg-WASM transcode-to-PCM (input is size-capped upstream)
var TIER_2_INPUT_CAP_MB_DEFAULT = 200;

// Tier-0 video-audio probe targets only files that LOOK like video. Pure
// audio files (mp3/wav/flac/etc.) always have audio by definition; skipping
// the probe for them keeps the no-cost-when-not-needed property.
var VIDEO_EXT_RE = /^(mp4|mov|m4v|3gp|3g2|webm|mkv|avi|wmv|flv|ts|m2ts|mts|mpg|mpeg|ogv|vob|asf|rm|rmvb|f4v)$/i;

// Errors we deliberately do NOT rescue — re-trying any tier won't help and
// would just spend battery. Cancellation, OOM, "no codec ever".
var FATAL_PATTERNS = [
    /aborted by user/i,
    /AbortError/i,
    /out of memory/i,
    /Cannot allocate/i,
    /allocation failed/i,
];

function isFatal(err) {
    if (!err) return false;
    // A file FileBytes has judged unreadable after its whole ladder (moved,
    // not on the device, or a browser that stopped reading — chat 714) is
    // unreadable to every tier: FFmpeg mounts the same dead handle. Going on
    // only downloads a 30 MB engine to fail again. A memory wall and a cancel
    // carry their own kinds and are matched by the patterns below.
    if (isDeadFile(err)) return true;
    var msg = (err.message || String(err)) + ' ' + (err.name || '');
    for (var i = 0; i < FATAL_PATTERNS.length; i++) {
        if (FATAL_PATTERNS[i].test(msg)) return true;
    }
    return false;
}

function isDeadFile(err) {
    var k = err && err.fileLostKind;
    return k === 'read_error' || k === 'not_found' || k === 'browser';
}

function ensureCtx(ctx) {
    // Require a REAL AudioContext (must expose decodeAudioData) — a caller that
    // passes a plain object (e.g. { context: ctx }) would otherwise sail through
    // the .state check and blow up in tier1Native with "x.decodeAudioData is not a
    // function". Reject it → allocate a fresh context instead of throwing.
    if (ctx && typeof ctx.decodeAudioData === 'function' && ctx.state !== 'closed') return ctx;
    try { return new (window.AudioContext || window.webkitAudioContext)(); }
    catch (_) { return null; }
}

function getExt(file) {
    return ((file && file.name) || 'f').split('.').pop().toLowerCase() || 'bin';
}

function browserKey() {
    var ua = navigator.userAgent || '';
    if (/YaBrowser/i.test(ua)) return 'yandex';
    if (/Edg\//i.test(ua))     return 'edge';
    if (/OPR\//i.test(ua))     return 'opera';
    if (/Firefox/i.test(ua))   return 'firefox';
    if (/Chrome/i.test(ua))    return 'chrome';
    if (/Safari/i.test(ua))    return 'safari';
    return 'other';
}

// Intermediate tier errors (0, 1, 1.5, 2) are DIAGNOSTIC — the 3-tier
// chain may still rescue this file via tier 1.5 / 2 / 3. Auto-stage('error')
// must NOT fire on these, otherwise the funnel reports a tool as "errored"
// even when decode ultimately succeeded (Olga #770, 78 events/4d on
// add-music-to-video, plus the same cohort polluted on every audio tool
// that uses AudioDecoderRobust). The `_no_auto_stage:1` flag is honored
// by __aeToolError in app.blade.php — the tool_error row still emits
// (diagnostics preserved), the deferred __stage('error') auto-call is
// skipped. Tier 3 (final-failure) does NOT pass this flag, so genuinely
// undecodable files still flip the funnel to error correctly.
function emitTierError(tier, slug, file, err) {
    try {
        var msg = ((err && err.message) || String(err) || '<empty>').substring(0, 200);
        if (window.__aeToolError) window.__aeToolError({
            error_type: 'decode_tier_failed',
            tier: tier,
            err_msg: msg,
            file_ext: getExt(file),
            file_type: (file && file.type) || '',
            file_size: (file && file.size) || 0,
            br: browserKey(),
            slug: slug || '',
            detail: (err && err.wcDetail) ? JSON.stringify(err.wcDetail).substring(0, 220) : undefined,
            _no_auto_stage: tier < 3 ? 1 : 0
        });
    } catch (_) {}
}

function emitRecovered(tier, slug, ms) {
    try {
        if (window.__ae) window.__ae('feature_use', {
            slug: slug || '',
            f: 'audio_decode_recovered',
            tier: tier,
            ms: ms
        });
    } catch (_) {}
}

function setLastError(tier, file, err) {
    try {
        window.__LAST_TOOL_ERROR = {
            tool: (window.__TOOL_SLUG || ''),
            stage: 'decode',
            reason: 'decode_tier_failed_t' + tier,
            msg: ((err && err.message) || String(err) || '<empty>').substring(0, 200),
            fileSize: (file && file.size) || 0,
            fileExt: getExt(file),
            fileType: (file && file.type) || '',
            ts: Date.now()
        };
    } catch (_) {}
}

// Wrap a promise with a timeout that throws so a hung decodeAudioData
// doesn't leave the user staring at a spinner forever.
function withTimeout(promise, ms, label) {
    return Promise.race([
        promise,
        new Promise(function(_, reject) {
            setTimeout(function() {
                var e = new Error(label + ' timed out after ' + ms + 'ms');
                e.name = 'TimeoutError';
                reject(e);
            }, ms);
        })
    ]);
}

function tier2InputCapBytes() {
    // Mirror __effectiveFileLimit('audio') if available, else fall back
    // to a conservative default. Above this we don't try Tier 2 because
    // FFmpeg-PCM expansion can OOM the tab.
    if (typeof window.__effectiveFileLimit === 'function') {
        try { return window.__effectiveFileLimit('audio'); }
        catch (_) {}
    }
    return TIER_2_INPUT_CAP_MB_DEFAULT * 1024 * 1024;
}

async function fileToArrayBuffer(file) {
    if (file instanceof ArrayBuffer) return file;
    // Use FileBytes (file-buffer-cache.js) when available — it caches the
    // bytes and protects against FILE_LOST when the OS revokes the handle
    // mid-operation. Falls back to file.arrayBuffer() if FileBytes isn't
    // loaded yet.
    if (window.FileBytes && typeof window.FileBytes.prime === 'function') {
        try {
            var primed = await window.FileBytes.prime(file);
            /* prime() answers null for files above its RAM cap (100 MB): it
               spilled them to OPFS and read only 64 KB. That null used to be
               handed straight to decodeAudioData, so tier 1 died with
               "buf is null" / "parameter 1 is not of type ArrayBuffer" on every
               file over the cap and the chain fell into a full FFmpeg
               transcode of the whole recording (measured 2026-09-04: a 967 MB
               WAV, 91 minutes decoded to PCM for a waveform picture). The cap
               is about CACHING, not about reading — read directly instead. */
            if (primed) return primed;
        }
        catch (eP) {
            // Judged dead after the whole ladder — a raw read of the same handle
            // can only fail again, or (Safari, chat 714) never return at all.
            if (isDeadFile(eP)) throw eP;
        }
    }
    return await file.arrayBuffer();
}

// Tier 0 — container-level "does this video have an audio stream?" probe.
//
// Closes a 469-events/24h cluster on /add-music-to-video where users uploaded
// silent videos (phone screen recordings, GoPro b-roll, no-mic camera) into
// the music slot. Without this check, the file flowed through Tier 1 (timeout
// 30 s on decodeAudioData), Tier 2 (FFmpeg WASM init + PCM extract that hits
// "Output file #0 does not contain any stream | Aborted()"), and Tier 3
// (AudioRecover declines because there is nothing to recover). User waited
// 30-60 s for a generic "could not load" toast that didn't say WHY.
//
// The shared __detectVideoAudio primitive (video-audio-probe.js) already does
// exactly this detection for video-trim — mp4box for mp4-family, FFmpeg probe
// for other containers, with confidence levels. We piggyback. Only acts on
// container-truth verdicts (confident: true) to avoid false-negative blocks
// from heuristic signals like webkitAudioDecodedByteCount = 0.
//
// Cost when probe library is unloaded or file is pure audio: ~0.1 ms (a
// single regex test). Cost when it runs: ~150-500 ms vs the 30+ s waste it
// replaces.
async function tier0NoAudioStreamProbe(file, slug) {
    if (!file || typeof window.__detectVideoAudio !== 'function') return;
    var ext = getExt(file);
    var fileType = (file && file.type) || '';
    var isVideo = (fileType.indexOf('video/') === 0) || VIDEO_EXT_RE.test(ext);
    if (!isVideo) return;
    var res;
    try {
        res = await window.__detectVideoAudio(file, { skipPlayback: true });
    } catch (_) {
        // Probe failure ≠ no audio. Fall through to the waterfall and let
        // Tier 1/2/3 give a verdict.
        return;
    }
    if (!res || res.hasAudio !== false || res.confident !== true) return;
    // Container truth: this video has no audio stream. Don't waste the WASM
    // waterfall — surface a typed error the caller can localise cleanly.
    try {
        if (window.__ae) window.__ae('feature_use', {
            slug: slug || '',
            f: 'audio_decode_blocked_no_audio',
            source: res.source || 'unknown',
            file_ext: ext
        });
    } catch (_) {}
    throw noAudioStreamError(file, res.source);
}

function noAudioStreamError(file, source) {
    var Klass = (window.ToolErrors && window.ToolErrors.NoAudioStreamError);
    if (Klass) return new Klass(file, source);
    var err = new Error('Video has no audio stream');
    err.name = 'NoAudioStreamError';
    err.reason = 'no_audio_stream';
    err.details = { source: source, fileName: file && file.name, fileType: (file && file.type) || '' };
    return err;
}

// Tier 0 — moov-missing probe for mp4-family files. Like
// tier0NoAudioStreamProbe, this short-circuits BEFORE Tier 1 attempts a
// decodeAudioData that we already know will fail. Saves the 30 s Tier 1
// timeout AND the 30 MB Tier 2 WASM dive AND the ~7 s Tier 3 recovery dance.
// Files with no `moov` box in either head or tail (mp4/m4a/m4b/m4r/m4p/3gp/
// 3gpp/mov) are empirically unrecoverable through any browser-native path —
// verified in tests/playwright/audio-recover-tier4-research.mjs.
//
// Placed at Tier 0 (not between tier 1 catch and tier 2 try, where this
// gate originally lived 2026-06-02) on the architectural lesson from
// tool_add_music_to_video.md: when a decode is guaranteed to fail,
// detect upstream of every tier — not between tiers.
async function tier0MoovMissingProbe(file, slug) {
    if (!file || !file.name) return;
    if (!window.AudioRecover || typeof window.AudioRecover.detectMoovMissing !== 'function') return;
    var fnLower = file.name.toLowerCase();
    if (!/\.(mp4|m4a|m4b|m4r|m4p|3gp|3gpp|mov)$/.test(fnLower)) return;
    var missing;
    try {
        missing = await window.AudioRecover.detectMoovMissing(file);
    } catch (_) {
        // Probe failure ≠ moov absent. Fall through; let the waterfall give a verdict.
        return;
    }
    if (!missing) return;
    try {
        if (window.__ae) window.__ae('feature_use', {
            slug: slug,
            f: 'moov_missing_repair_cta',
            ext: getExt(file),
            via: 'tier0_robust_decoder',
        });
    } catch (_) {}
    var hintShown = false;
    try {
        if (typeof window.AudioRecover.showUnrecoverableHint === 'function') {
            window.AudioRecover.showUnrecoverableHint('moov_missing', file);
            hintShown = true;
        }
    } catch (_) {}
    var err = new Error('moov atom missing — file unrecoverable through any browser-native path');
    err.name = 'MoovMissingError';
    err.reason = 'moov_missing';
    err.alreadyReported = true;
    err.expected = true;
    // uiHintShown — signals that a typed, user-facing UI (here:
    // AudioRecover.showUnrecoverableHint, a localised warning toast with a
    // "Open Repair Audio" CTA) has already been rendered for THIS error.
    // Downstream tool catch handlers should NOT emit their generic "file
    // load failed" toast/telemetry on top, which buries the actionable hint
    // and double-counts in dashboards (29 events / 14 d on /volume alone,
    // surfaced by 2026-06-19 telemetry audit). Separate from `alreadyReported`
    // (telemetry-only) because some typed errors (WASM_UNSUPPORTED) carry
    // alreadyReported BUT have no upstream UI — for those, the catch SHOULD
    // still surface its localised message.
    if (hintShown) err.uiHintShown = true;
    throw err;
}

// ── header-truth duration probe ─────────────────────────────────────────
// Reads ONLY container headers (≤512 KB) and answers "how many PCM bytes
// will this file become once decoded?". File size is the WRONG axis for
// that question: a 157 MB 24-bit/96 kHz FLAC is 4 minutes (≈92 MB decoded
// into a 48 kHz context) while a 180 MB CD-quality FLAC is an hour
// (≈650 MB decoded). Every size×multiplier heuristic mis-ranks that pair
// in BOTH directions (prod case: user 3450, 2 GB RAM, 2026-07-29).
// Returns { durSec, channels, sampleRate, method, exact } or null when the
// container can't be parsed cheaply (callers must fail OPEN on null).
/* ── Ogg: Vorbis / Opus identification header ─────────────────────────────
 *
 * Added 2026-08-15 (report W_SA "Wrong sample rate"). Before this, every
 * container outside FLAC/WAV/MP3 returned null, and the two consumers then
 * fell back to the AudioContext rate — i.e. the LISTENER'S SOUND CARD:
 *   • the file-info row printed the card's rate as if it were the file's
 *     (measured on prod: the same 44.1 kHz .ogg read "44.1" on a 44.1 kHz
 *     card and "22.1" on a 22.05 kHz one);
 *   • decodeRateFor could not pin the decode, so the audio was silently
 *     resampled — the exact class the hi-res fix closed for FLAC/WAV/MP3.
 *
 * Layout: 'OggS'(4) ver(1) flags(1) granule(8) serial(4) seq(4) crc(4)
 *         segments(1) segTable(segments) then the first packet.
 * Vorbis id packet: 0x01 'vorbis' ver(4) channels(1) rate(4 LE)
 * Opus id packet:   'OpusHead' ver(1) channels(1) preSkip(2) inputRate(4 LE)
 *   ⚠️ Opus ALWAYS decodes at 48 kHz; `inputRate` only records what the
 *   encoder was fed. Reporting the input rate would be a lie about the audio
 *   that actually exists, so Opus is pinned to 48000 by the format.
 */
function oggAudioInfo(head) {
    try {
        var segments = head.getUint8(26);
        var pkt = 27 + segments;
        if (pkt + 16 > head.byteLength) return null;
        function str(off, n) {
            var s = '';
            for (var i = 0; i < n; i++) s += String.fromCharCode(head.getUint8(off + i));
            return s;
        }
        if (str(pkt, 8) === 'OpusHead') {
            var opusCh = head.getUint8(pkt + 9);
            return { durSec: 0, channels: opusCh || 2, sampleRate: 48000, method: 'ogg-opus', exact: false };
        }
        if (head.getUint8(pkt) === 0x01 && str(pkt + 1, 6) === 'vorbis') {
            var ch = head.getUint8(pkt + 11);
            var rate = head.getUint32(pkt + 12, true);
            if (rate > 0) return { durSec: 0, channels: ch || 2, sampleRate: rate, method: 'ogg-vorbis', exact: false };
        }
    } catch (_) { /* malformed page — fail open */ }
    return null;
}

/* ── MP4 / M4A with the index at the END of the file ─────────────────────
 * Recorders that write as they go (iPhone Voice Memos, most Android voice
 * recorders, screen recorders) put moov AFTER mdat; only `-movflags
 * +faststart` moves it to the front. The head slice therefore sees
 * [ftyp][free?][mdat…] and nothing else, and until 2026-09-12 the probe
 * answered null for exactly the files people record on their phones — the
 * whole-decode gate then FAILED OPEN on the one platform that dies of it
 * (support #6831: a 3 h 25 min Voice Memo on /speech-restorer, page killed).
 * Walk the top-level boxes by seeking (a handful of 16-byte reads for any
 * file size, the same walk media-audio-probe.js does), then parse the moov
 * slice with the walker the head path uses. Capped: a moov over 16 MB is
 * not an audio recording's index.
 */
async function mp4AudioInfoTail(file) {
    if (!file || !file.slice || !(file.size > 0)) return null;
    var MOOV_CAP = 16 * 1048576;
    try {
        var pos = 0, hops = 0;
        while (pos + 8 <= file.size && hops++ < 512) {
            var hb = new DataView(await file.slice(pos, Math.min(file.size, pos + 16)).arrayBuffer());
            if (hb.byteLength < 8) break;
            var size = hb.getUint32(0), hdr = 8;
            var type = String.fromCharCode(hb.getUint8(4), hb.getUint8(5), hb.getUint8(6), hb.getUint8(7));
            if (!/^[\x20-\x7E]{4}$/.test(type)) break;
            if (size === 1) {
                if (hb.byteLength < 16) break;
                size = hb.getUint32(8) * 4294967296 + hb.getUint32(12); hdr = 16;
            } else if (size === 0) {
                size = file.size - pos;
            }
            if (size < hdr) break;
            if (type === 'moov') {
                if (size > MOOV_CAP) return null;
                var moov = new DataView(await file.slice(pos, Math.min(file.size, pos + size)).arrayBuffer());
                return mp4AudioInfo(moov, pos);
            }
            pos += size;
        }
    } catch (_) { /* unreadable → fail open, as the head path does */ }
    return null;
}

/* ── MP4 / M4A: mdhd timescale of the audio track ─────────────────────────
 * Walks moov → trak → mdia → { hdlr (is this the SOUND track?), mdhd }.
 * mdhd's timescale is the audio sample rate for an audio media handler.
 * Only the head slice is available, which is enough for faststart files and
 * for the common case where moov precedes mdat; otherwise we fail open.
 *
 * `baseOffset` is where the view starts in the file (0 for the head slice,
 * the box offset for a moov read from the tail): the fragmented path below
 * needs to know where moov ENDS in the file.
 *
 * ⚠️⚠️ A FRAGMENTED mp4 (moov carries `mvex`) keeps its samples in moof+mdat
 * pairs AFTER moov, and moov's own durations describe only what moov itself
 * holds: nothing (empty_moov — mdhd 0) or the FIRST fragment. Measured
 * 26.09.2026 on real ffmpeg output of a 24:33 video: the empty_moov file read
 * 0 s, and the frag_keyframe one read **2.04 s** — a confident «fits» that
 * let a 24-minute file past the length wall in every browser, because
 * probeDuration trusts a positive header answer and never asks the element.
 * (Support 22.09 + 26.09, /auto-captions, Android/Яндекс «5 раз пытаюсь»,
 * «👎👎👎»: the length was unknown before both runs of the same 24:33 mp4, the
 * preview never got metadata on that phone, and each run died with the tab.)
 * So with mvex present the moov durations are NOT the answer: the result
 * carries `frag` and the caller reads the fragments (mp4FragmentedDuration).
 */
function mp4AudioInfo(head, baseOffset) {
    baseOffset = baseOffset || 0;
    try {
        var end = head.byteLength;
        function str(off, n) {
            var s = '';
            for (var i = 0; i < n; i++) s += String.fromCharCode(head.getUint8(off + i));
            return s;
        }
        var found = null;
        // Per-trak scratch: hdlr says whether this trak is SOUND, mdhd carries
        // the media clock. Before 2026-09-02 the walk took the FIRST stsd it
        // met with a non-zero 16-bit field at +24 — on a video-first mp4 that
        // is the visual entry's WIDTH (1920/576…), which then went to
        // OfflineAudioContext() as a sample rate and failed open by luck.
        var trakIsSound = false, trakDur = 0, trakRate = 0, trakChans = 0, trakTs = 0, trakId = 0, trakCodec = '', trakEdit = -1;
        var mvhdTs = 0, mvhdDur = 0, hasMvex = false, mehdDur = 0, trex = {}, moovEnd = 0, moovTruncated = false;
        /* How many sound traks the movie holds. Every decoder here takes ONE —
           the first, or FFmpeg's "best" (the default flag, then the channel
           count: on a tie, the first) — and an iOS screen recording with the
           microphone on has been seen with two equal stereo AAC traks, app
           audio first (2 kb/s of silence) and the voice second (ffmpeg trac
           #7088, iOS 11). Whether that still ships is not known; this count is
           how robust() finds out (`multi_sound_tracks`). */
        var soundTraks = 0, soundCodecs = [];
        (function walk(start, stop, depth) {
            var p = start;
            while (p + 8 <= stop && depth < 8) {
                var size = head.getUint32(p);
                var type = str(p + 4, 4);
                if (size < 8) return;
                var bodyStart = p + 8, bodyEnd = Math.min(p + size, stop);
                if (type === 'trak') {
                    trakIsSound = false; trakDur = 0; trakRate = 0; trakChans = 0; trakTs = 0; trakId = 0; trakCodec = ''; trakEdit = -1;
                    walk(bodyStart, bodyEnd, depth + 1);
                    if (trakIsSound) { soundTraks++; soundCodecs.push(trakCodec || '?'); }
                    if (!found && trakIsSound && trakRate > 0) {
                        found = { rate: trakRate, channels: trakChans || 2, durSec: trakDur, trackId: trakId, timescale: trakTs, codec: trakCodec, editUnits: trakEdit };
                    }
                } else if (type === 'moov') {
                    moovEnd = baseOffset + p + size;
                    if (p + size > stop) moovTruncated = true;
                    walk(bodyStart, bodyEnd, depth + 1);
                    return;   // after moov come mdat/moof — nothing this parse reads
                } else if (type === 'mdia' || type === 'minf' || type === 'stbl' || type === 'edts') {
                    walk(bodyStart, bodyEnd, depth + 1);
                } else if (type === 'elst' && bodyStart + 8 <= bodyEnd) {
                    /* The track's PLAYED length: the sum of its edits that show
                       media (media_time ≥ 0), in the movie clock — what FFmpeg,
                       the WebCodecs tier (editWindow) and Chromium's own decoder
                       hand back. FullBox(4) entry_count(4), then per entry
                       segment_duration and media_time (4+4 in v0, 8+8 in v1)
                       and media_rate(4). An empty edit (−1) is a delay the
                       decoders do not render. */
                    var ev = head.getUint8(bodyStart), en = head.getUint32(bodyStart + 4), q = bodyStart + 8, sum = 0;
                    for (var ei = 0; ei < en; ei++) {
                        var segDur, mediaTime;
                        if (ev === 1) {
                            if (q + 20 > bodyEnd) break;
                            segDur = head.getUint32(q) * 4294967296 + head.getUint32(q + 4);
                            mediaTime = head.getInt32(q + 8) * 4294967296 + head.getUint32(q + 12);
                            q += 20;
                        } else {
                            if (q + 12 > bodyEnd) break;
                            segDur = head.getUint32(q);
                            mediaTime = head.getInt32(q + 4);
                            q += 12;
                        }
                        if (mediaTime >= 0) sum += segDur;
                    }
                    trakEdit = sum;
                } else if (type === 'mvex') {
                    hasMvex = true;
                    walk(bodyStart, bodyEnd, depth + 1);
                } else if (type === 'mehd' && bodyStart + 8 <= bodyEnd) {
                    // FullBox(4) fragment_duration: 64 bits in v1, 32 in v0 — in mvhd units
                    var mv = head.getUint8(bodyStart);
                    mehdDur = (mv === 1 && bodyStart + 12 <= bodyEnd)
                        ? head.getUint32(bodyStart + 4) * 4294967296 + head.getUint32(bodyStart + 8)
                        : head.getUint32(bodyStart + 4);
                } else if (type === 'trex' && bodyStart + 16 <= bodyEnd) {
                    // FullBox(4) track_ID(4) default_sample_description_index(4) default_sample_duration(4)
                    trex[head.getUint32(bodyStart + 4)] = head.getUint32(bodyStart + 12);
                } else if (type === 'mvhd' && bodyStart + 4 <= bodyEnd) {
                    var hv = head.getUint8(bodyStart);
                    if (hv === 1 && bodyStart + 32 <= bodyEnd) {
                        mvhdTs = head.getUint32(bodyStart + 20);
                        mvhdDur = head.getUint32(bodyStart + 24) * 4294967296 + head.getUint32(bodyStart + 28);
                    } else if (bodyStart + 20 <= bodyEnd) {
                        mvhdTs = head.getUint32(bodyStart + 12);
                        mvhdDur = head.getUint32(bodyStart + 16);
                    }
                } else if (type === 'tkhd' && bodyStart + 4 <= bodyEnd) {
                    // FullBox(4) creation+modification (4+4 in v0, 8+8 in v1) track_ID(4)
                    var idOff = head.getUint8(bodyStart) === 1 ? bodyStart + 20 : bodyStart + 12;
                    if (idOff + 4 <= bodyEnd) trakId = head.getUint32(idOff);
                } else if (type === 'hdlr' && bodyStart + 12 <= bodyEnd) {
                    // FullBox(4) pre_defined(4) handler_type(4)
                    // ⚠️ Only a 'soun' handler decides, and nothing un-decides
                    // it. A QuickTime .mov carries a SECOND hdlr inside minf —
                    // the data handler ('dhlr', subtype 'alis' / 'url '), after
                    // the media one. Assigning here let it overwrite 'soun', and
                    // until 15.10.2026 every .mov — every iPhone video, Mac
                    // screen recording, camera MOV — answered null: no rate, no
                    // channel count, no length oracle, no memory gate. Report
                    // 09.10 (/mono-to-stereo, iPhone): the channel count stayed
                    // 0, the tool read 0 as mono, took the left channel and
                    // handed back silence. The flag is reset per trak above.
                    if (str(bodyStart + 8, 4) === 'soun') trakIsSound = true;
                } else if (type === 'mdhd' && bodyStart + 4 <= bodyEnd) {
                    var ver = head.getUint8(bodyStart);
                    if (ver === 1 && bodyStart + 32 <= bodyEnd) {
                        var ts1 = head.getUint32(bodyStart + 20);
                        var du1 = head.getUint32(bodyStart + 24) * 4294967296 + head.getUint32(bodyStart + 28);
                        if (ts1 > 0) trakTs = ts1;
                        if (ts1 > 0 && du1 > 0) trakDur = du1 / ts1;
                    } else if (bodyStart + 20 <= bodyEnd) {
                        var ts0 = head.getUint32(bodyStart + 12);
                        var du0 = head.getUint32(bodyStart + 16);
                        if (ts0 > 0) trakTs = ts0;
                        if (ts0 > 0 && du0 > 0 && du0 !== 0xFFFFFFFF) trakDur = du0 / ts0;
                    }
                } else if (type === 'stsd' && bodyStart + 16 <= bodyEnd) {
                    // stsd: version/flags(4) entryCount(4) then entries; an audio
                    // sample entry carries the rate as 16.16 fixed at offset 24.
                    var e = bodyStart + 8;
                    if (e + 32 <= bodyEnd) {
                        var rate = head.getUint16(e + 8 + 24);
                        var chans = head.getUint16(e + 8 + 16);
                        /* QuickTime SoundDescription v2 (version at +16 == 2) —
                           what a .mov gets for a rate over 65535 or more than two
                           channels, from ffmpeg and from Apple: the 16.16 field
                           holds 1.0 and the channel field 3, both placeholders.
                           The truth follows: sizeOfStructOnly(4), the rate as a
                           float64 at +40, the channel count as a uint32 at +48.
                           Read as v0, a 96 kHz .mov was "1 Hz, 3 channels". */
                        if (head.getUint16(e + 16) === 2 && e + 52 <= bodyEnd) {
                            var rate2 = head.getFloat64(e + 40);
                            var chans2 = head.getUint32(e + 48);
                            if (rate2 >= 1000 && rate2 <= 768000) rate = Math.round(rate2);
                            if (chans2 >= 1 && chans2 <= 64) chans = chans2;
                        }
                        if (rate > 0) { trakRate = rate; trakChans = chans; trakCodec = str(e + 4, 4); }
                    }
                }
                p += size;
            }
        })(0, end, 0);
        if (found && found.rate > 0) {
            // durSec is the media duration (mdhd) — encoder priming included,
            // so `exact` stays false; it is an ORACLE for "did the decode come
            // back the right length", not a sample count.
            var out = { durSec: found.durSec || 0, channels: found.channels, sampleRate: found.rate, method: 'mp4', exact: false, codec: found.codec || '',
                        soundTracks: soundTraks, soundCodecs: soundCodecs };
            /* ── …unless an edit list says what is PLAYED (2026-10-30) ──
               A clip cut by stream copy (LosslessCut, `ffmpeg -ss … -c copy`,
               a phone's own trim) keeps the samples back to the keyframe
               before the cut and hides them with an edit list. mdhd counts
               them: 15.01 s on a 12.08-s clip. FFmpeg decoded the clip's AC-3
               correctly — 12.0 s — and the ±20 % gate below threw it away as a
               lying decoder; every tier after it failed too, and /video-editor
               exported that clip SILENT, with no error (stand
               video-editor-export-lanes.mjs; prod: decode_duration_mismatch
               expected > got by the pre-roll). The played length only ever
               shortens the media one — a priming edit trims 21–48 ms. */
            if (found.editUnits >= 0 && mvhdTs > 0) {
                var played = found.editUnits / mvhdTs;
                if (played > 0 && (!(out.durSec > 0) || played < out.durSec)) { out.durSec = played; out.edited = true; }
            }
            if (moovTruncated) out.truncated = true;
            if (hasMvex) {
                // Fragmented: moov's own durations are only the start. The
                // movie-extends header, when the muxer knew the length, is the
                // whole answer; otherwise the fragments have to be read.
                out.durSec = 0;
                if (mehdDur > 0 && mvhdTs > 0) { out.durSec = mehdDur / mvhdTs; out.method = 'mp4-mehd'; }
                else out.frag = { trackId: found.trackId, timescale: found.timescale || found.rate,
                                  trexDur: trex[found.trackId] || 0, moovEnd: moovEnd };
            } else if (!(out.durSec > 0) && mvhdTs > 0 && mvhdDur > 0 && mvhdDur !== 0xFFFFFFFF) {
                out.durSec = mvhdDur / mvhdTs;   // the track clock is silent, the movie clock is not
            }
            return out;
        }
    } catch (_) { /* truncated / moov at tail — fail open */ }
    return null;
}

/* ── mp4FragmentedDuration(file, frag) — the END of the sound track in a
 * fragmented mp4, in seconds; 0 = could not tell (the caller stays honest).
 *
 * 1. mfra: the file's last box is mfro (16 bytes) naming the mfra size; its
 *    tfra lists the random-access points with their moof offsets — the last
 *    entry for our track is the last fragment. Two small reads.
 * 2. No mfra (a writer that skips the trailer, DASH, a cut-off recording):
 *    the LAST moof is found by scanning the TAIL backwards for the moof+mfhd
 *    signature (`[size]moof[size=16]mfhd` — 12 bytes that compressed payload
 *    does not produce by accident), not by hopping over every box from the
 *    front: a 24-min file with 2-s fragments is ~1500 top-level boxes, i.e.
 *    ~1500 reads, which a phone pays in seconds. The window grows only when
 *    the last fragment itself is bigger than it (a long, high-bitrate one).
 * The end = tfdt (baseMediaDecodeTime) + Σ sample durations of that traf's
 * truns, in the track timescale. Without tfdt the moof alone cannot tell
 * where it sits — the tfra time is used when there is one, else unknown.
 */
async function mp4FragmentedDuration(file, frag) {
    if (!file || !file.slice || !(file.size > 16) || !frag || !(frag.timescale > 0)) return 0;
    var size = file.size;
    async function read(a, b) { return new DataView(await file.slice(a, Math.min(size, b)).arrayBuffer()); }
    function fcc(dv, o) {
        return String.fromCharCode(dv.getUint8(o), dv.getUint8(o + 1), dv.getUint8(o + 2), dv.getUint8(o + 3));
    }
    async function endAtMoof(off, fallbackTime) {
        var h = await read(off, off + 8);
        if (h.byteLength < 8 || fcc(h, 4) !== 'moof') return 0;
        var sz = h.getUint32(0);
        if (sz < 16 || sz > 4 * 1048576 || off + sz > size) return 0;
        var r = mp4MoofTrackEnd(await read(off, off + sz), frag.trackId, frag.trexDur);
        if (!r) return 0;
        if (r.hasTfdt) return r.end;
        return fallbackTime > 0 ? fallbackTime + r.sum : 0;
    }
    // 1. mfra at the tail
    try {
        var t = await read(size - 16, size);
        if (t.byteLength === 16 && fcc(t, 4) === 'mfro') {
            var mfraSize = t.getUint32(12);
            if (mfraSize >= 16 && mfraSize <= Math.min(size, 16 * 1048576)) {
                var last = mp4TfraLast(await read(size - mfraSize, size), frag.trackId);
                if (last && last.moofOffset > 0 && last.moofOffset < size) {
                    var endU = await endAtMoof(last.moofOffset, last.time);
                    if (endU > 0) return endU / frag.timescale;
                }
            }
        }
    } catch (_) { /* fall through to the scan */ }
    // 2. the last moof, found from the tail
    try {
        var floor = frag.moovEnd > 0 ? frag.moovEnd : 0;
        var windows = [2 * 1048576, 8 * 1048576, 32 * 1048576];
        for (var w = 0; w < windows.length; w++) {
            var a = Math.max(floor, size - windows[w]);
            var dv = await read(a, size);
            for (var k = dv.byteLength - 16; k >= 0; k--) {
                // 'moof' at k+4, an mfhd of 16 bytes at k+8
                if (dv.getUint8(k + 4) !== 0x6D || dv.getUint8(k + 5) !== 0x6F ||
                    dv.getUint8(k + 6) !== 0x6F || dv.getUint8(k + 7) !== 0x66) continue;
                if (dv.getUint32(k + 8) !== 16 || fcc(dv, k + 12) !== 'mfhd') continue;
                var e2 = await endAtMoof(a + k, 0);
                if (e2 > 0) return e2 / frag.timescale;
                // a last fragment without our track (video only) — keep looking back
            }
            if (a === floor) break;   // everything after moov was scanned
        }
    } catch (_) { /* unreadable → unknown */ }
    return 0;
}

/* moof → the traf of `trackId`: tfdt + Σ trun sample durations. */
function mp4MoofTrackEnd(dv, trackId, trexDur) {
    try {
        var end = dv.byteLength;
        function fcc(o) {
            return String.fromCharCode(dv.getUint8(o), dv.getUint8(o + 1), dv.getUint8(o + 2), dv.getUint8(o + 3));
        }
        var p = 8;   // moof body
        while (p + 8 <= end) {
            var sz = dv.getUint32(p), type = fcc(p + 4);
            if (sz < 8) return null;
            if (type === 'traf') {
                var q = p + 8, qEnd = Math.min(p + sz, end);
                var id = 0, defDur = 0, tfdt = -1, sum = 0;
                while (q + 8 <= qEnd) {
                    var bs = dv.getUint32(q), bt = fcc(q + 4), b = q + 8;
                    if (bs < 8) break;
                    if (bt === 'tfhd' && b + 8 <= qEnd) {
                        var hf = dv.getUint32(b) & 0xFFFFFF, o = b + 8;
                        id = dv.getUint32(b + 4);
                        if (hf & 0x01) o += 8;   // base_data_offset
                        if (hf & 0x02) o += 4;   // sample_description_index
                        if ((hf & 0x08) && o + 4 <= qEnd) defDur = dv.getUint32(o);
                    } else if (bt === 'tfdt' && b + 8 <= qEnd) {
                        tfdt = (dv.getUint8(b) === 1 && b + 12 <= qEnd)
                            ? dv.getUint32(b + 4) * 4294967296 + dv.getUint32(b + 8)
                            : dv.getUint32(b + 4);
                    } else if (bt === 'trun' && b + 8 <= qEnd) {
                        var rf = dv.getUint32(b) & 0xFFFFFF, n = dv.getUint32(b + 4), r = b + 8;
                        if (rf & 0x01) r += 4;   // data_offset
                        if (rf & 0x04) r += 4;   // first_sample_flags
                        if (rf & 0x100) {
                            var rec = 4 * (1 + ((rf & 0x200) ? 1 : 0) + ((rf & 0x400) ? 1 : 0) + ((rf & 0x800) ? 1 : 0));
                            for (var i = 0; i < n && r + 4 <= qEnd; i++, r += rec) sum += dv.getUint32(r);
                        } else {
                            sum += n * (defDur || trexDur || 0);
                        }
                    }
                    q += bs;
                }
                if (id === trackId) return { end: (tfdt >= 0 ? tfdt : 0) + sum, sum: sum, hasTfdt: tfdt >= 0 };
            }
            p += sz;
        }
    } catch (_) {}
    return null;
}

/* mfra → the tfra of `trackId` → its last entry {time, moofOffset}. */
function mp4TfraLast(dv, trackId) {
    try {
        var end = dv.byteLength;
        function fcc(o) {
            return String.fromCharCode(dv.getUint8(o), dv.getUint8(o + 1), dv.getUint8(o + 2), dv.getUint8(o + 3));
        }
        if (end < 16 || fcc(4) !== 'mfra') return null;
        var p = 8;
        while (p + 8 <= end) {
            var sz = dv.getUint32(p), type = fcc(p + 4);
            if (sz < 8) break;
            if (type === 'tfra' && p + 24 <= end) {
                var b = p + 8, v = dv.getUint8(b), id = dv.getUint32(b + 4), lens = dv.getUint32(b + 8);
                var n = dv.getUint32(b + 12), r = b + 16;
                // length_size_of_traf_num / _trun_num / _sample_num: 2 bits each, +1 bytes
                var rec = (v === 1 ? 16 : 8) + (((lens >> 4) & 3) + 1) + (((lens >> 2) & 3) + 1) + ((lens & 3) + 1);
                if (id === trackId && n > 0 && r + n * rec <= Math.min(p + sz, end)) {
                    var lr = r + (n - 1) * rec;
                    return {
                        time: v === 1 ? dv.getUint32(lr) * 4294967296 + dv.getUint32(lr + 4) : dv.getUint32(lr),
                        moofOffset: v === 1 ? dv.getUint32(lr + 8) * 4294967296 + dv.getUint32(lr + 12) : dv.getUint32(lr + 4)
                    };
                }
            }
            p += sz;
        }
    } catch (_) {}
    return null;   // not our track's index — the caller scans the tail instead
}

/* ── One source of format knowledge ───────────────────────────────────────
 *
 * This probe and public/js/audio-header-probe.js grew independently and DRIFTED:
 * AIFF lived only in the sibling, ogg/mp4 only here. The cost was not academic —
 * the SAME defect was reported by users three times, each time for whichever
 * container the probe in that path happened not to know:
 *   2026-07-16  AIFF 24/96 silently became 44.1/48 kHz on every output path
 *   2026-07-30  hi-res FLAC/WAV/MP3 resampled to the sound card's rate
 *   2026-08-15  ogg / m4a / opus reported (and decoded at) the card's rate
 * Each was fixed by teaching ONE probe ONE more container. That is the loop this
 * delegation ends: whatever either module knows, both now know.
 *
 * Order: our own parsers first (no dependency, already covers ~97 % of real
 * uploads — measured over 4043 files), then the sibling for anything left.
 * Both stay fail-open: unknown container → null, and the CALLER must then not
 * claim to know the file's rate.
 */
async function headerAudioInfoDelegate(file) {
    var P = (typeof window !== 'undefined' && window.AudioHeaderProbe)
         || (typeof self !== 'undefined' && self.AudioHeaderProbe) || null;
    if (!P || typeof P.probe !== 'function') return null;
    try {
        var r = await P.probe(file);
        if (!r || !(r.sampleRate > 0)) return null;
        return {
            durSec: r.durationSec > 0 ? r.durationSec : 0,
            channels: r.channels || 2,
            sampleRate: r.sampleRate,
            method: 'probe-' + (r.format || 'unknown'),
            exact: !!r.durationSec
        };
    } catch (_) { return null; }
}

async function headerAudioInfo(file) {
    var own = await headerAudioInfoOwn(file);
    if (own && own.sampleRate > 0) return own;
    return await headerAudioInfoDelegate(file);
}

async function headerAudioInfoOwn(file) {
    if (!file || !file.slice) return null;
    var head;
    try {
        head = new DataView(await file.slice(0, Math.min(file.size, 524288)).arrayBuffer());
    } catch (_) { return null; }
    if (head.byteLength < 12) return null;

    function fourcc(off) {
        return String.fromCharCode(head.getUint8(off), head.getUint8(off + 1),
                                   head.getUint8(off + 2), head.getUint8(off + 3));
    }

    // FLAC — STREAMINFO is mandatory and always the FIRST metadata block:
    // 4-byte "fLaC" marker, 4-byte block header, then 34 bytes of STREAMINFO.
    if (fourcc(0) === 'fLaC' && head.byteLength >= 42) {
        var sr = (head.getUint8(18) << 12) | (head.getUint8(19) << 4) | (head.getUint8(20) >> 4);
        var ch = ((head.getUint8(20) >> 1) & 0x07) + 1;
        // total samples: 36 bits — low 4 bits of byte 21 + bytes 22..25.
        var totalHi = head.getUint8(21) & 0x0F;
        var totalLo = head.getUint32(22);
        var totalSamples = totalHi * 4294967296 + totalLo;
        if (sr > 0 && totalSamples > 0) {
            return { durSec: totalSamples / sr, channels: ch, sampleRate: sr, method: 'flac', exact: true };
        }
        return null; // streaming-encoded FLAC with unknown length
    }

    // WAV — RIFF chunk walk inside the head: fmt_ gives rate/channels/block
    // align, data gives payload bytes (bounded by what the file holds).
    if (fourcc(0) === 'RIFF' && fourcc(8) === 'WAVE') {
        var off = 12, fmt = null, dataBytes = 0;
        while (off + 8 <= head.byteLength) {
            var id = fourcc(off);
            var sz = head.getUint32(off + 4, true);
            if (id === 'fmt ' && off + 24 <= head.byteLength) {
                fmt = {
                    channels: head.getUint16(off + 10, true),
                    sampleRate: head.getUint32(off + 12, true),
                    byteRate: head.getUint32(off + 16, true)
                };
            } else if (id === 'data') {
                // The bytes ON DISK, not the size field. Streamed WAVs write
                // 0, 0xFFFFFFFF or (arecord to a pipe) 0x7FFFFFFF there, and a
                // cut-off copy keeps the full size of the original. Both
                // engines decode what is actually there — measured 29.09.2026,
                // Chrome 154 and Firefox: a 203 s WAV cut to 60 % decodes to
                // 121.8 s, a 0x7FFFFFFF header to 203 s — while the field made
                // this probe answer 203 s and 12 174 s («3 h 22 min», a false
                // wall in front of a three-minute song). The comment this
                // replaces claimed decodeAudioData allocates for the field; it
                // does not.
                var payload = Math.max(0, file.size - (off + 8));
                dataBytes = (sz > 0 && sz <= payload) ? sz : payload;
                break;
            }
            off += 8 + sz + (sz & 1);
        }
        if (fmt && fmt.byteRate > 0 && dataBytes > 0) {
            return { durSec: dataBytes / fmt.byteRate, channels: fmt.channels || 2,
                     sampleRate: fmt.sampleRate || 44100, method: 'wav', exact: true };
        }
        return null;
    }

    // Known non-MP3 containers must bail out BEFORE the MP3 frame-sync scan:
    // raw AAC/ALAC payload inside an mp4 box happily contains 0xFF Ex byte
    // pairs, and a false sync would fabricate a duration for a container we
    // did not actually parse (caught by the m4a case in
    // music-mastering-lowmem-lifecycle.mjs). null = the gate fails open.
    // Ogg (Vorbis / Opus) — the identification header lives in the FIRST page,
    // right after the 27-byte page header + segment table.
    if (fourcc(0) === 'OggS') return oggAudioInfo(head);

    // MP4 / M4A — mdhd carries the media timescale, which for an audio track
    // IS its sample rate; stsd/mp4a carries it too. Read whichever we reach.
    if (fourcc(4) === 'ftyp') {
        var inHead = mp4AudioInfo(head, 0);
        // moov after mdat — the iPhone Voice Memos / most Android recorder
        // layout. The head slice cannot see it; a few 16-byte seeks can. The
        // same seeks read a moov that started in the head but did not end there.
        var mp4 = (inHead && !inHead.truncated) ? inHead : ((await mp4AudioInfoTail(file)) || inHead);
        if (mp4 && mp4.frag) {
            var fragSec = 0;
            try { fragSec = await mp4FragmentedDuration(file, mp4.frag); } catch (_) { fragSec = 0; }
            if (fragSec > 0) { mp4.durSec = fragSec; mp4.method = 'mp4-frag'; }
            else mp4.method = 'mp4-frag-unknown';
            delete mp4.frag;
        }
        if (mp4) delete mp4.truncated;
        return mp4;
    }

    // MP3 — skip ID3v2, find the first frame header, prefer the Xing/Info
    // frame count (exact for VBR and CBR), else estimate from bitrate.
    // Scan only when the file plausibly IS an mp3 (name/mime/ID3 tag) — a
    // frame-sync match alone is too weak a signature to trust on arbitrary
    // binary input.
    var looksMp3 = /\.mp3(\W|$)/i.test((file && file.name) || '') ||
                   /mpeg|mp3/i.test((file && file.type) || '') ||
                   fourcc(0).slice(0, 3) === 'ID3';
    if (!looksMp3) return null;
    var scanStart = 0;
    if (fourcc(0).slice(0, 3) === 'ID3' && head.byteLength >= 10) {
        var tagSize = ((head.getUint8(6) & 0x7F) << 21) | ((head.getUint8(7) & 0x7F) << 14) |
                      ((head.getUint8(8) & 0x7F) << 7)  |  (head.getUint8(9) & 0x7F);
        scanStart = 10 + tagSize;
    }
    var MP3_RATES = [44100, 48000, 32000];
    var MP3_BITRATES_V1L3 = [0, 32, 40, 48, 56, 64, 80, 96, 112, 128, 160, 192, 224, 256, 320, 0];
    var MP3_BITRATES_V2L3 = [0, 8, 16, 24, 32, 40, 48, 56, 64, 80, 96, 112, 128, 144, 160, 0];
    for (var p = scanStart; p + 4 <= head.byteLength && p < scanStart + 65536; p++) {
        if (head.getUint8(p) !== 0xFF || (head.getUint8(p + 1) & 0xE0) !== 0xE0) continue;
        var b1 = head.getUint8(p + 1), b2 = head.getUint8(p + 2), b3 = head.getUint8(p + 3);
        var verBits = (b1 >> 3) & 0x03;              // 3 = MPEG1, 2 = MPEG2, 0 = MPEG2.5
        var layerBits = (b1 >> 1) & 0x03;            // 1 = Layer III
        if (verBits === 1 || layerBits !== 1) continue;
        var srIdx = (b2 >> 2) & 0x03; if (srIdx === 3) continue;
        var brIdx = (b2 >> 4) & 0x0F; if (brIdx === 0 || brIdx === 15) continue;
        var mpeg1 = verBits === 3;
        var srMp3 = MP3_RATES[srIdx] / (mpeg1 ? 1 : (verBits === 2 ? 2 : 4));
        var chMp3 = (((b3 >> 6) & 0x03) === 3) ? 1 : 2;
        var samplesPerFrame = mpeg1 ? 1152 : 576;
        // Xing / Info header sits after the side info of this first frame.
        var side = mpeg1 ? (chMp3 === 1 ? 17 : 32) : (chMp3 === 1 ? 9 : 17);
        var xOff = p + 4 + side;
        if (xOff + 16 <= head.byteLength) {
            var tag = fourcc(xOff);
            if ((tag === 'Xing' || tag === 'Info') && (head.getUint32(xOff + 4) & 0x01)) {
                var frames = head.getUint32(xOff + 8);
                if (frames > 0) {
                    return { durSec: frames * samplesPerFrame / srMp3, channels: chMp3,
                             sampleRate: srMp3, method: 'mp3-xing', exact: true };
                }
            }
        }
        var kbps = (mpeg1 ? MP3_BITRATES_V1L3 : MP3_BITRATES_V2L3)[brIdx];
        if (kbps > 0) {
            return { durSec: (file.size - p) * 8 / (kbps * 1000), channels: chMp3,
                     sampleRate: srMp3, method: 'mp3-cbr', exact: false };
        }
        break;
    }
    return null;
}

// Tier 0 — memory-budget probe (OPT-IN via robust(file, ctx, {memGuard})).
//
// Prod class this closes (2026-07-29, user 3450 — 2 GB RAM, no-SIMD CPU,
// hi-res FLAC albums): a file whose DECODED size × pipeline working set
// cannot fit device memory doesn't fail fast — it drags the whole machine
// through 4..12 minutes of swap (decodeAudioData timed out after 232 s,
// tier-2 rescue 735 s, worklet load 94 s) and then dies with an opaque
// "Array buffer allocation failed" / "Unable to decode". Same lesson as the
// moov probe above: when the outcome is known from the header, say so
// UPFRONT — honestly, with numbers — instead of letting every tier burn.
//
// guard = { multiplier: N } — caller's whole-pipeline working-set factor
// (how many decoded-size copies the tool holds at peak; measured per tool,
// see tests/playwright/music-mastering-lowmem-lifecycle.mjs for the
// music-mastering calibration). Encoded bytes are accounted separately
// (tier-1 holds FileBytes cache + a decode copy).
// Fails OPEN: unparseable container / missing duration → no block.
// Budget = COMMIT tolerance (RAM + acceptable swap), not free physical RAM.
// Calibrated against the one 2 GB field device with rich telemetry (user
// 3450): his repeatable successes need ≈870 MB predicted (4-min hi-res FLAC,
// ×6 multiplier + encoded), his machine-killing failures need ≥4.2 GB (hour
// album). 1.5 × deviceMemory separates those classes with margin on both
// sides: dm=2 → 3072 MB. Predictions between the calibrated points are
// allowed (fail-open bias) — memory_budget_blocked telemetry carries
// need/budget so the line can be tuned from the field.
var MEM_BUDGET_FLOOR_MB = 1024, MEM_BUDGET_PER_GB_MB = 1536;
// The iOS WKWebView tab is killed at ~500-700 MB whatever the phone's RAM is
// (layouts/app.blade.php → window.__memBudget, the owner of that number).
// Until 2026-09-12 this function read `navigator.deviceMemory || 8`, which is
// exactly the line __memBudget exists to replace: WebKit never reports
// deviceMemory, so an iPhone was budgeted at 12 GB here and the tier-0 gate
// could not fire on the one platform whose ceiling is fixed and low. 700 is
// the top of the measured range, and it is where the field puts
// /speech-restorer on iOS: restorations up to 10.7 min complete, none over
// 15 (RU, 90 days to 2026-09-11) — the multiplier ceiling lands there.
var IOS_TAB_BUDGET_MB = 700;
/* The user's abort, by NAME — «Aborted by user» alone also matched a dying
   runtime's «Aborted()» in message checks (window.__isUserCancel). */
function _abortedByUser() { var e = new Error('Aborted by user'); e.name = 'AbortError'; return e; }
function memBudgetMB() {
    var o = (typeof window !== 'undefined' && (window.__memBudgetMB | 0));
    if (o > 0) return o;                       // test seam, same idiom as __tier1BaseMs
    var B = (typeof window !== 'undefined') ? window.__memBudget : null;
    if (B && typeof B.constrained === 'function' && B.constrained()) return IOS_TAB_BUDGET_MB;
    var dm = (B && typeof B.gb === 'function') ? B.gb() : (navigator.deviceMemory || 8);
    return Math.max(MEM_BUDGET_FLOOR_MB, dm * MEM_BUDGET_PER_GB_MB);
}
/* ── A big mp4-family file is decoded WITHOUT reading it (29.09.2026) ──────
 * The whole-track tier 1.5 read the file into memory (`file.arrayBuffer()`,
 * plus MP4Box's own copy): a 3 GB phone video — 26:49 of 1080p120 with a
 * 39 MB soundtrack — cannot be read at all (no JS buffer is larger than
 * 2 GiB), and both budget formulas charged it twice its size before trying,
 * so /video-volume and /add-music-to-video refused it as «too long for this
 * device» at any length. Above LAZY_WHOLE_MIN_BYTES the whole track goes
 * through WebCodecsAudioDecode.decodeRange (moov + the audio units in runs of
 * ≤ 8 MB, the video between them never read — the reader /slideshow-maker
 * already uses for windows), and the budget charges what is really held.
 * Below it nothing changes: the proven whole-file path keeps every file that
 * works today. G.711 is not read by decodeRange — such a file above the line
 * goes down the old ladder (docs/why/primitives/audio-decode-robust.md). */
var LAZY_WHOLE_MIN_BYTES = 1073741824;
function lazyWholeTrack(file) {
    var W = (typeof window !== 'undefined') ? window.WebCodecsAudioDecode : null;
    return !!(file && (file.size || 0) > LAZY_WHOLE_MIN_BYTES && W && typeof W.decodeRange === 'function' &&
        typeof W.isMp4Family === 'function' && W.isMp4Family(file) && typeof AudioDecoder !== 'undefined');
}
/* Encoded bytes a decode holds in memory besides the PCM: the FileBytes copy
   and the tier's own read (2× the file), or — for a lazily read track — one
   read window and the moov. */
/* Tier 1.5's whole-track read: the lazy reader above the line (see
   lazyWholeTrack), the whole-file one below it. The same PCM either way —
   presentation time, the edit list honoured, the rate measured by the
   container's clock. */
function tier15Decode(file, ctx) {
    if (lazyWholeTrack(file)) return window.WebCodecsAudioDecode.decodeRange(file, ctx, { from: 0 });
    return window.WebCodecsAudioDecode.decode(file, ctx);
}
function encodedHeldMB(file) {
    return lazyWholeTrack(file) ? 16 : ((file && file.size) || 0) * 2 / 1048576;
}
async function tier0MemoryBudgetProbe(file, ctx, slug, guard) {
    var mult = (guard && guard.multiplier > 0) ? guard.multiplier : 2.5;
    var info = await headerAudioInfo(file);
    if (!info || !(info.durSec > 0)) return;   // header unknown → fail open
    // Budget against the rate the buffer will REALLY be decoded at. Tier 1
    // now pins the decode to the source rate (see decodeRateFor), so a
    // 96 kHz file costs 96 kHz worth of Float32 — budgeting it at the card's
    // 48 kHz under-counted hi-res sources exactly 2×.
    var ctxRate = (ctx && ctx.sampleRate) || 48000;
    var shape = decodedShape({ sampleRate: info.sampleRate > 0 ? info.sampleRate : ctxRate, channels: info.channels, codec: info.codec });
    var decRate = shape.rate;
    var ch = shape.ch;
    var decodedMB = info.durSec * decRate * ch * 4 / 1048576;
    var encodedMB = encodedHeldMB(file);
    var needMB = decodedMB * mult + encodedMB;
    var budgetMB = memBudgetMB();
    if (needMB <= budgetMB) return;
    try {
        if (window.__ae) window.__ae('tool_error', {
            slug: slug || '',
            error_type: 'memory_budget_blocked',
            need: Math.round(needMB), budget: Math.round(budgetMB),
            decoded: Math.round(decodedMB), dur: Math.round(info.durSec),
            ch: ch, dm: navigator.deviceMemory || 0,
            method: info.method, file_ext: getExt(file), file_size: file.size || 0
        });
    } catch (_) {}
    var err = new Error('Decoded audio would not fit device memory: need ~' +
        Math.round(needMB) + ' MB, budget ~' + Math.round(budgetMB) + ' MB');
    err.name = 'MemoryBudgetError';
    err.reason = 'memory_budget';
    err.expected = true;          // device constraint, not a code bug (Sentry)
    err.alreadyReported = true;   // telemetry above; caller renders the toast
    err.needMB = Math.round(needMB);
    err.budgetMB = Math.round(budgetMB);
    err.durSec = Math.round(info.durSec);
    err.deviceGB = navigator.deviceMemory || 0;
    throw err;
}

// Tier 1 — native decodeAudioData with timeout.
//
// timbrica-core.js installs a global wrapper on AudioContext.prototype.decodeAudioData
// that auto-runs an implicit FFmpeg fallback on rejection. That wrapper is great for
// tools that haven't been swept to AudioDecoder yet, but it would double-run our
// Tier 2 (FFmpeg) inside Tier 1, blowing budget for no benefit. Setting
// `window.__suppressDecodeFallback = true` is the documented opt-out: the wrapper
// becomes a thin passthrough to the native decode, leaving recovery to us.
/**
 * Which context should Tier 1 decode on?
 *
 * `decodeAudioData` ALWAYS resamples to the sampleRate of the context it runs
 * on, and a tool's `new AudioContext()` inherits the OUTPUT DEVICE rate. So a
 * 48 kHz file opened on a 44.1 kHz sound card came out 44.1 kHz — silently,
 * with no setting to prevent it, and invisibly to anyone developing on a
 * 48 kHz machine. Reported 2026-07-30 on /equalizer: 24-bit/48 kHz in,
 * 16-bit/44.1 kHz out.
 *
 * When the header states a rate, decode inside an OfflineAudioContext pinned
 * to it, so the file keeps its own rate end to end. Playback is unaffected:
 * a source-rate buffer played through a differently-clocked context is
 * resampled by the browser at output time (measured — a 48 kHz buffer in a
 * 44.1 kHz context keeps its exact length and pitch).
 *
 * Returns the caller's ctx whenever the rate is unknown, already equal, or
 * the platform refuses that rate — never worse than the old behaviour.
 */
async function decodeRateFor(file, ctx, info) {
    try {
        if (!info) info = await headerAudioInfo(file);
        var srcRate = info && info.sampleRate > 0 ? info.sampleRate : 0;
        if (!srcRate) return ctx;
        // An mp4 sample entry at ≤ 24 kHz is, in practice, the CORE rate of an
        // HE-AAC stream whose decoder emits 2× that (SBR). Pinning the offline
        // context to the header rate then throws the whole SBR band away —
        // measured 2026-09-02: a 32 kHz HE-AAC v2 came back 16 kHz, 13.568 s,
        // "correct" length, half the bandwidth. Decode at the context rate
        // instead; a genuine 22.05 kHz AAC-LC merely gets upsampled.
        if (info.method === 'mp4' && srcRate <= 24000) return ctx;
        var ctxRate = (ctx && ctx.sampleRate) || 0;
        if (srcRate === ctxRate) return ctx;
        var OfflineCtx = window.OfflineAudioContext || window.webkitOfflineAudioContext;
        if (!OfflineCtx) return ctx;
        return new OfflineCtx(1, 1, srcRate);
    } catch (_) {
        return ctx;   // probe/construct failed — fail open to the old path
    }
}

async function tier1Native(file, ctx, signal, hdrInfo) {
    if (signal && signal.aborted) throw _abortedByUser();
    ctx = await decodeRateFor(file, ctx, hdrInfo);
    var ab = await fileToArrayBuffer(file);
    if (signal && signal.aborted) throw _abortedByUser();
    // ab.slice(0) — some browsers detach the buffer after decodeAudioData,
    // and Tier 2 might want the original bytes. Float32 for Uint8Array
    // arrives via FileBytes; decodeAudioData wants ArrayBuffer so unwrap.
    var copy;
    if (ab instanceof Uint8Array) copy = ab.buffer.slice(ab.byteOffset, ab.byteOffset + ab.byteLength);
    else if (ab && ab.slice)      copy = ab.slice(0);
    else                          copy = ab;
    var prevSuppress = window.__suppressDecodeFallback;
    window.__suppressDecodeFallback = true;
    try {
        return await withTimeout(ctx.decodeAudioData(copy), tier1TimeoutMs(file), 'decodeAudioData');
    } catch (e) {
        // decodeAudioData internally allocates an AudioBuffer (PCM × sampleRate × ch
        // × seconds × 4 B/sample). On low-RAM devices this allocation can throw
        // RangeError "Array buffer allocation failed" (TIMBRICA-FRONTEND-95). Mark
        // as expected so Sentry beforeSend drops it (this is a device constraint,
        // not a code bug); mark as isFatal so the orchestrator's Tier 2/3 fallback
        // doesn't retry — they will all hit the same heap ceiling.
        if (e instanceof RangeError || /allocation failed|out of memory/i.test(String(e && e.message || ''))) {
            e.expected = true;
            e.isFatal = true;
        }
        throw e;
    } finally {
        window.__suppressDecodeFallback = prevSuppress;
    }
}

// Tier 2 — FFmpeg WASM PCM passthrough → decodeAudioData on the WAV.
async function tier2Ffmpeg(file, ctx, signal, opts) {
    // A `typeof FFmpegProcessor === 'undefined'` gate does NOT work here:
    // timbrica-core installs a queue-stub under that exact name on every
    // page, so the gate always passes, init() is called on the stub, and the
    // deferred promise parks until its 15 s "module never loaded" timeout —
    // burning 15 s before Tier 3 even starts on any page that doesn't
    // statically include ffmpeg-processor.js. Pull the real impl in first
    // (__ensureFFmpeg appends the script on demand) and only then decide the
    // tier is genuinely unavailable. Same recipe the tool blades migrated to
    // on 2026-05-07; the shared decoder was missed in that pass.
    if (window.__ensureFFmpeg) {
        try { await window.__ensureFFmpeg(); } catch (_) {}
    }
    // Fall back to the raw typeof check only where the layout helpers are
    // absent (standalone pages), so this can never regress a working tier.
    var hasReal = window.__hasRealFFmpeg
        ? window.__hasRealFFmpeg()
        : (typeof FFmpegProcessor !== 'undefined');
    if (!hasReal) {
        throw new Error('FFmpegProcessor unavailable');
    }
    if (signal && signal.aborted) throw _abortedByUser();
    var size = (file && file.size) || 0;
    if (size > tier2InputCapBytes()) {
        throw new Error('File too large for Tier 2 PCM passthrough');
    }
    if (!FFmpegProcessor.isReady()) {
        await FFmpegProcessor.init(function(p) {
            if (opts.onProgress) try { opts.onProgress(0.05 + p * 0.4); } catch (_){}
        });
    }
    if (signal && signal.aborted) throw _abortedByUser();
    var inputName = 'input.' + getExt(file);
    var blob = (file instanceof File) ? file
             : (file instanceof Blob) ? file
             : new Blob([file], { type: 'application/octet-stream' });
    // Output flags: -vn drops video, -sn drops subtitles (matches extract-audio
    // convention). Input-side: -err_detect ignore_err is the critical addition —
    // FFmpeg's demuxer probes EVERY stream's codec parameters before honouring
    // output flags like -vn. For files with AV1/HEVC video streams the probe
    // returns "unspecified pixel format" which Aborted()s the whole process
    // before -vn ever applies. ignore_err lets demux skip past stream-level
    // codec errors, so the audio stream can still be found and extracted.
    // Telemetry on add-music-to-video: -vn-only fix closed OOM cases but ~280
    // small-MP4 files / 4 d still Aborted because of AV1/HEVC video streams
    // mixed with normal audio (phone recordings, screen captures, etc.).
    // Forward REAL FFmpeg decode progress (the `progress` worker event, 0..1)
    // into the caller's onProgress so a no-progress stall watchdog re-arms on
    // genuine proof-of-life. Without this, tier 2 emitted only two coarse
    // milestones (0.4 before / 0.85 after) spanning the ENTIRE single-threaded
    // WASM decode — which on a long file (2–3 min, per ffmpeg-processor.js:457)
    // is a >45 s progress blackout that false-fired the uploader watchdog and
    // discarded a decode that would have succeeded (workflow wf_1729e989,
    // 2026-06-20). FFmpegProcessor.onProgress registers a single handler that
    // replaces any prior one; the next FFmpeg consumer re-registers its own.
    if (typeof FFmpegProcessor.onProgress === 'function') {
        try {
            FFmpegProcessor.onProgress(function (p) {
                if (opts.onProgress) try { opts.onProgress(0.4 + Math.max(0, Math.min(1, p || 0)) * 0.45); } catch (_){}
            });
        } catch (_){}
    }
    // headSeconds (decodeHead entry point): `-t N` stops the decoder after N
    // seconds of output — WASM memory stays bounded no matter how long the
    // recording is. Without it (robust() tiers) the whole file is decoded.
    // Keep the source layout and rate through the rescue path too. These were
    // pinned to `-ac 2 -ar 44100`, so ANY file that fell back to Tier 2 (native
    // decode failing — common on Yandex Browser and older Android) came back
    // folded to stereo and resampled, no matter what it really was. The
    // downstream parser reads both from the header and accepts 1..32 channels
    // at 8–384 kHz, so widening them costs nothing there.
    //
    // Bit depth stays s16 on purpose: the fast parser handles only s16 and f32,
    // and f32 would double an intermediate WAV on the very path we take because
    // the file was already too much for the native decoder. Rescue first.
    var t2Info = null;
    try { t2Info = await headerAudioInfo(file); } catch (_) {}
    // A raw stream (AC-3, DTS, E-AC-3, MLP…) has no header this reader knows,
    // and the old default of 2 then FOLDED it: a 5.1 AC-3 came back as a
    // stereo buffer, tier 2 reported success, and /channel-converter exported
    // "keep layout" as two channels without a word (measured 2026-09-13). The
    // FFmpeg instance is being loaded for this tier anyway, so ask it.
    if (!(t2Info && t2Info.channels >= 1)) {
        try {
            if (typeof FFmpegProcessor !== 'undefined' && typeof FFmpegProcessor.probeMeta === 'function') {
                var t2Meta = await FFmpegProcessor.probeMeta(file);
                if (t2Meta && t2Meta.audioChannels >= 1) {
                    t2Info = Object.assign({}, t2Info || {}, { channels: t2Meta.audioChannels });
                    if (!(t2Info.durSec > 0) && t2Meta.duration > 0) t2Info.durSec = t2Meta.duration;
                    if (!(t2Info.sampleRate > 0) && t2Meta.audioSampleRate > 0) t2Info.sampleRate = t2Meta.audioSampleRate;
                }
            }
        } catch (_) { /* no probe → the old default, never worse */ }
    }
    var t2Ch = (t2Info && t2Info.channels >= 1 && t2Info.channels <= 32) ? t2Info.channels : 2;
    var t2Rate = (t2Info && t2Info.sampleRate >= 8000 && t2Info.sampleRate <= 384000)
        ? t2Info.sampleRate : 44100;
    // Keeping the source layout is right, but it is not free: the intermediate
    // WAV is duration x rate x channels x 2 bytes, and it has to exist whole.
    // Until 2026-08-29 the header read returned nothing for MP4-past-512-KB and
    // for Matroska, so those files quietly came back as stereo and the size
    // question never arose; now that they answer, a 5.1 film would ask for three
    // times the intermediate on the very path we are on BECAUSE the file was
    // already too much for the native decoder. Fold to stereo rather than OOM,
    // and say so — a silent fold is what this code was fixed for once already.
    var t2Dur = (t2Info && t2Info.durSec > 0) ? t2Info.durSec : 0;
    if (t2Ch > 2 && t2Dur > 0) {
        var pcmMB = t2Dur * t2Rate * t2Ch * 2 / 1048576;
        var budgetMB = Math.max(256, (navigator.deviceMemory || 4) * 1024 * 0.35);
        if (pcmMB > budgetMB) {
            try {
                (typeof window !== 'undefined' && window.__ae) && window.__ae('feature_use', {
                    f: 'tier2_layout_folded', ch: t2Ch, estMB: Math.round(pcmMB), budgetMB: Math.round(budgetMB)
                });
            } catch (_) {}
            t2Ch = 2;
        }
    }
    t2Ch = String(t2Ch); t2Rate = String(t2Rate);
    var outArgs = ['-vn', '-sn', '-ac', t2Ch, '-ar', t2Rate, '-c:a', 'pcm_s16le'];
    if (opts.headSeconds > 0) outArgs = ['-t', String(opts.headSeconds)].concat(outArgs);

    /* Resync a broken MPEG stream BEFORE FFmpeg sees it.
     *
     * 233 production failures on /repair-audio all ended in "Error setting option
     * time_base to value 1/0 | Error configuring filter graph". That is not a
     * filter fault: FFmpeg could not derive a sample rate, because the bytes it
     * was pointed at were not a frame header — an ID3 length running past EOF, a
     * saved error page glued in front, a zeroed first block from a failing card.
     * Forcing `-ar` on the OUTPUT cannot help; the INPUT has to become parseable.
     *
     * Optional by design: without the module loaded this is a no-op, so the ~30
     * other tools sharing this decoder are unaffected until they opt in. */
    var t2Blob = blob;
    try {
        if (window.__mp3Resync && /\.mp3$|\.mp2$|\.mpga$/i.test(inputName || '')) {
            var rs = await window.__mp3Resync.resyncMp3(blob);
            if (rs.plan.action === 'trim') {
                t2Blob = rs.blob;
                if (window.__ae) window.__ae('feature_use', {
                    feature: 'mp3_resync', reason: rs.plan.reason, dropped: rs.plan.droppedBytes });
            }
        }
    } catch (_) { /* a resync that throws must never block the normal path */ }

    var pcm = await withTimeout(FFmpegProcessor.process(
        t2Blob,
        outArgs,
        'decoded.wav',
        inputName,
        ['-err_detect', 'ignore_err', '-analyzeduration', '5M', '-probesize', '5M']
    ), TIER_2_PROCESS_TIMEOUT_MS, 'tier2 ffmpeg process');
    if (!pcm || !pcm.blob || pcm.blob.size < 100) {
        throw new Error('FFmpeg returned empty / undersized output');
    }
    // Mark the FFmpeg-produced WAV as a non-final intermediate so the
    // global `URL.createObjectURL` auto-tracker in app.blade.php does NOT
    // emit a `tool_complete{src:'blob', type:'audio/wav', size:N}` 2 s
    // later (FFmpegProcessor.process calls createObjectURL on its return
    // value, which the tracker would otherwise treat as the tool's output).
    // Telemetry root cause: 11 phantom audio/wav >500 MB tool_complete
    // events / 14 d on /volume from decoder-rescued sessions (e.g. sid
    // 48ec92b2 — got toast_error but dashboards counted "success"). The
    // 2 s debounce window means we just need to register BEFORE the timer
    // fires; `pcm.url` already exists by now so the next createObjectURL
    // call wouldn't be patched anyway, but the patch checks the BLOB at
    // emission time — registering `pcm.blob` is the correct signal.
    try {
        if (typeof window.__skipBlobAutoTrack === 'function') {
            window.__skipBlobAutoTrack(pcm.blob);
        }
    } catch (_) {}
    if (opts.onProgress) try { opts.onProgress(0.85); } catch (_){}
    var wavAb = await pcm.blob.arrayBuffer();
    // 2026-07-08 (comment #938): the WAV we just produced OURSELVES (FFmpeg,
    // pcm_s16le) went through ANOTHER decodeAudioData with the old fixed
    // 30 s timeout — a 678 MB WAV from a 90-min audiobook needs longer than
    // that on a 4-core box, so tier 2 "failed" AFTER FFmpeg had already
    // succeeded. There is nothing to decode: it's raw PCM with a 44-byte
    // header. Parse it directly into an AudioBuffer — microseconds of header
    // math + one Int16→Float32 copy, no decoder, no timeout class at all.
    // decodeAudioData stays as the fallback for exotic WAV shapes the parser
    // declines (non-PCM codec inside WAV, malformed chunks).
    var direct = wavPcmToAudioBuffer(wavAb, ctx);
    if (direct) return direct;
    // Same suppress dance — the wrapper would otherwise re-run FFmpeg if
    // even the clean PCM WAV somehow rejected, which has no chance of
    // helping (we just produced that WAV ourselves with FFmpeg).
    var prevSuppress = window.__suppressDecodeFallback;
    window.__suppressDecodeFallback = true;
    try {
        return await withTimeout(ctx.decodeAudioData(wavAb), tier1TimeoutMs(pcm.blob), 'tier2 re-decode');
    } catch (e) {
        // Same allocation-failure guard as tier1Native (TIMBRICA-FRONTEND-95).
        if (e instanceof RangeError || /allocation failed|out of memory/i.test(String(e && e.message || ''))) {
            e.expected = true;
            e.isFatal = true;
        }
        throw e;
    } finally {
        window.__suppressDecodeFallback = prevSuppress;
    }
}

// Parse a PCM WAV (RIFF/WAVE, fmt tag 1 = integer PCM or 3 = float32) into
// an AudioBuffer without decodeAudioData. Returns null when the container
// isn't a shape we're sure about — caller falls back to the real decoder.
// Supports 16-bit int (FFmpeg tier-2 output: pcm_s16le) and 32-bit float
// (AudioRecover's audioBufferToWav output), any channel count.
function wavPcmToAudioBuffer(ab, ctx) {
    try {
        if (!ab || ab.byteLength < 44) return null;
        var dv = new DataView(ab);
        // 'RIFF' … 'WAVE'
        if (dv.getUint32(0, false) !== 0x52494646 || dv.getUint32(8, false) !== 0x57415645) return null;
        var pos = 12, fmt = null, dataOff = -1, dataLen = 0;
        while (pos + 8 <= ab.byteLength) {
            var id = dv.getUint32(pos, false);
            var len = dv.getUint32(pos + 4, true);
            if (id === 0x666D7420) {          // 'fmt '
                fmt = {
                    tag:      dv.getUint16(pos + 8, true),
                    channels: dv.getUint16(pos + 10, true),
                    rate:     dv.getUint32(pos + 12, true),
                    bits:     dv.getUint16(pos + 22, true),
                };
                // WAVE_FORMAT_EXTENSIBLE (0xFFFE): the real format is the first
                // two bytes of the SubFormat GUID — 1 = PCM, 3 = IEEE float —
                // and the samples are laid out exactly like the plain tags.
                // Every multichannel WAV FFmpeg writes is EXTENSIBLE, so until
                // this the parser declined ALL of them and handed them to
                // decodeAudioData — which refuses the channel masks Chromium does
                // not map to a layout of its own (5.1 back 0x3F, 5.0 back 0x37,
                // quad side 0x603, 3.1 0x107, 7.1 wide 0xFF; measured 2026-09-13).
                // A 5.1 WAV in the most common layout failed every tier.
                if (fmt.tag === 0xFFFE && len >= 40 && pos + 8 + 26 <= ab.byteLength) {
                    fmt.tag = dv.getUint16(pos + 8 + 24, true);
                }
            } else if (id === 0x64617461) {   // 'data'
                dataOff = pos + 8;
                // Some writers leave data-chunk len = 0 / 0xFFFFFFFF for
                // streamed output; trust the actual byte range instead.
                dataLen = Math.min(len || (ab.byteLength - dataOff), ab.byteLength - dataOff);
                break;                        // data is last in FFmpeg output
            }
            pos += 8 + len + (len & 1);       // chunks are word-aligned
        }
        if (!fmt || dataOff < 0 || dataLen <= 0) return null;
        if (fmt.channels < 1 || fmt.channels > 32 || fmt.rate < 8000 || fmt.rate > 384000) return null;
        var isInt = fmt.tag === 1 && (fmt.bits === 16 || fmt.bits === 24 || fmt.bits === 32);
        var isF32 = fmt.tag === 3 && fmt.bits === 32;
        if (!isInt && !isF32) return null;    // exotic (ADPCM, 8-bit, float64…): let decodeAudioData judge
        var bytesPerSample = fmt.bits / 8;
        var frameBytes = bytesPerSample * fmt.channels;
        var frames = Math.floor(dataLen / frameBytes);
        if (frames < 1) return null;
        var n = frames * fmt.channels;
        // Typed views need an aligned offset; a WAV with extra chunks before
        // 'data' can put it on an odd byte, so copy just the samples out.
        var src = ab, off = dataOff;
        if (bytesPerSample !== 3 && (dataOff % bytesPerSample) !== 0) {
            src = ab.slice(dataOff, dataOff + n * bytesPerSample); off = 0;
        }
        var buf = ctx.createBuffer(fmt.channels, frames, fmt.rate);
        var C = fmt.channels, ch, i, s, dst;
        if (isF32) {
            var f32 = new Float32Array(src, off, n);
            for (ch = 0; ch < C; ch++) { dst = buf.getChannelData(ch); for (i = 0, s = ch; i < frames; i++, s += C) dst[i] = f32[s]; }
        } else if (fmt.bits === 16) {
            var i16 = new Int16Array(src, off, n);
            for (ch = 0; ch < C; ch++) { dst = buf.getChannelData(ch); for (i = 0, s = ch; i < frames; i++, s += C) dst[i] = i16[s] / 32768; }
        } else if (fmt.bits === 32) {
            var i32 = new Int32Array(src, off, n);
            for (ch = 0; ch < C; ch++) { dst = buf.getChannelData(ch); for (i = 0, s = ch; i < frames; i++, s += C) dst[i] = i32[s] / 2147483648; }
        } else {                               // 24-bit: no typed array, read the three bytes
            var u8 = new Uint8Array(src, off, n * 3);
            for (ch = 0; ch < C; ch++) {
                dst = buf.getChannelData(ch);
                for (i = 0, s = ch * 3; i < frames; i++, s += C * 3) {
                    dst[i] = ((u8[s] | (u8[s + 1] << 8) | (u8[s + 2] << 16)) << 8 >> 8) / 8388608;
                }
            }
        }
        return buf;
    } catch (_) {
        return null;   // any surprise → real decoder decides
    }
}

// Tier 3 — AudioRecover.tryRepair → decodeAudioData on the repaired WAV.
async function tier3Recover(file, ctx, signal, opts) {
    if (!window.AudioRecover || typeof window.AudioRecover.tryRepair !== 'function') {
        throw new Error('AudioRecover unavailable');
    }
    if (signal && signal.aborted) throw _abortedByUser();
    if (!(file instanceof File) && !(file instanceof Blob)) {
        // AudioRecover expects a File-like with .name; raw ArrayBuffer
        // can't be repaired without one (no MIME / ext hint).
        throw new Error('Tier 3 requires File/Blob input');
    }
    // Synthesize a "FFmpeg failed" error that AudioRecover treats as
    // recoverable. Its tryRepair handles size cap, moov-missing detection,
    // session memoization. Returns null (not throws) when it declines.
    var stub = new Error('Tier 3 retry: tier 1+2 failed, container suspected');
    var repaired = await window.AudioRecover.tryRepair(file, {
        origError: stub,
        triggerOp: 'audio_decoder_tier3'
    });
    if (!repaired) throw new Error('AudioRecover declined to retry');
    // Same auto-tracker guard as Tier 2: AudioRecover internally builds a
    // PCM WAV blob and may call createObjectURL on it, which would otherwise
    // fire a phantom `tool_complete{src:'blob', type:'audio/wav'}` 2 s later.
    try {
        if (typeof window.__skipBlobAutoTrack === 'function') {
            window.__skipBlobAutoTrack(repaired);
        }
    } catch (_) {}
    if (opts.onProgress) try { opts.onProgress(0.9); } catch (_){}
    var wavAb = await repaired.arrayBuffer();
    // Direct PCM parse first — same rationale as tier 2 (comment #938):
    // AudioRecover's repaired output IS a WAV we built; re-decoding it
    // through decodeAudioData only re-introduces the timeout class.
    var direct3 = wavPcmToAudioBuffer(wavAb, ctx);
    if (direct3) return direct3;
    var prevSuppress = window.__suppressDecodeFallback;
    window.__suppressDecodeFallback = true;
    try {
        return await withTimeout(ctx.decodeAudioData(wavAb), tier1TimeoutMs(repaired), 'tier3 re-decode');
    } catch (e) {
        // Final tier — allocation failure here means the audio is too large for
        // this device. Mark expected + fatal (TIMBRICA-FRONTEND-95).
        if (e instanceof RangeError || /allocation failed|out of memory/i.test(String(e && e.message || ''))) {
            e.expected = true;
            e.isFatal = true;
        }
        throw e;
    } finally {
        window.__suppressDecodeFallback = prevSuppress;
    }
}

/* ── The decode is part of the run the person is waiting for ─────────────
 * A whole-file decode is the first thing that kills a phone tab on a long
 * recording — and it happens BEFORE the tool opens its run bracket, so the
 * page died with no record and no note (the class __runLedger was built for;
 * support #6831). Bracket every decode in the same ledger, phase 'decode':
 * a renderer killed in here now leaves the same localStorage record, the
 * same run_lost_on_reload event and the same «choose the file again» note.
 * A run that is already open owns the record; a run that opens DURING the
 * decode takes it over (the bracket never closes a record a run holds).
 */
function openDecodeLedger() {
    var noop = function () {};
    try {
        var L = (typeof window !== 'undefined') ? window.__runLedger : null;
        if (!L || typeof L.open !== 'function' || typeof L.active !== 'function' || L.active()) return noop;
        L.open({ phase: 'decode' });
        return function () {
            try {
                var M = window.__runMark;
                if (M && typeof M.active === 'function' && M.active()) return;   // a run took the record over
                L.close();
            } catch (_) {}
        };
    } catch (_) { return noop; }
}
/* The decode ends when the file is judged unreadable — whatever read of its own
   it is waiting on. Support chat 714 (23.10.2026, Safari 26.6.1): the browser
   rejected two reads of a picked mp3 and never answered the next ones; this
   chain sat in headerAudioInfo's slice read for 644 s, holding the run ledger
   (screen awake, «точно уйти?») under «Читаю файл… 5 %». FileBytes owns the
   bounded pick-time read and its verdict; racing it here ends the decode with
   the same typed FILE_LOST the uploader shows, instead of a deadline in each of
   the dozen places this chain reads the file. */
function untilFileJudged(file) {
    var FB = (typeof window !== 'undefined') ? window.FileBytes : null;
    if (!FB || typeof FB.whenUnreadable !== 'function' || !(file instanceof Blob)) return null;
    try { return FB.whenUnreadable(file); } catch (_) { return null; }
}
async function robust(file, ctx, opts) {
    var closeLedger = openDecodeLedger();
    var judged = null;
    try {
        judged = untilFileJudged(file);
        var work = robustInner(file, ctx, opts);
        if (!judged) return await work;
        work.catch(function () {});   // settled by the verdict first — its own end is nobody's news
        return await Promise.race([work, judged.then(null, function (eDead) {
            setLastError(0, file, eDead);
            throw eDead;
        })]);
    } finally {
        try { if (judged && judged.release) judged.release(); } catch (_) {}
        closeLedger();
    }
}
async function robustInner(file, ctx, opts) {
    opts = opts || {};
    var slug = opts.slug || (window.__TOOL_SLUG || '');
    var signal = opts.signal || null;
    var t0 = Date.now();

    if (!file) {
        var e = new Error('AudioDecoderRobust.robust called without a file');
        emitTierError(0, slug, file, e);
        throw e;
    }
    ctx = ensureCtx(ctx);
    if (!ctx) throw new Error('AudioContext unavailable on this device');

    // Tier 0 — fast-path rejects for files we already know will fail every
    // tier. Both probes are typed-error fatal: re-throw to the caller's
    // typed-error handler instead of paying the 30 s Tier 1 timeout + the
    // 30 MB Tier 2 WASM dive + the ~7 s Tier 3 recovery dance.
    if (!opts.audioStreamChecked) {
        try {
            await tier0NoAudioStreamProbe(file, slug);
        } catch (e0) {
            if (e0 && e0.name === 'NoAudioStreamError') {
                setLastError(0, file, e0);
                throw e0;
            }
        }
    }
    try {
        await tier0MoovMissingProbe(file, slug);
    } catch (eM) {
        if (eM && eM.name === 'MoovMissingError') {
            setLastError(0, file, eM);
            throw eM;
        }
    }
    // Tier 0 — memory-budget probe, OPT-IN (opts.memGuard = {multiplier}).
    // Probe failures other than the typed verdict fall through to the
    // waterfall, same contract as the two probes above.
    if (opts.memGuard) {
        try {
            await tier0MemoryBudgetProbe(file, ctx, slug, opts.memGuard);
        } catch (eG) {
            if (eG && eG.name === 'MemoryBudgetError') {
                setLastError(0, file, eG);
                throw eG;
            }
        }
    }
    /* ── Default whole-buffer gate (2026-09-12, Sentry JS) ──────────────────
       A plain robust() caller IS a whole-buffer tool — 46 of them on this
       date — and it is exactly the caller that dies: /volume fell inside the
       decoder («Array buffer allocation failed») on a file its machine could
       not hold, because nobody had asked the length first. The primitive is
       now safe by default: the length is read BEFORE the decode (headers,
       tail moov, then the media element) and judged against THIS device's
       memory. An ordinary file costs one header read and never sees a
       dialog; only a file past the ceiling reaches the shared gate, which is
       where the tier-snapshot wait lives. decodeGated has already judged and
       passes `gated:true`; memGuard keeps its own opt-in probe. */
    if (!opts.memGuard && !opts.gated) {
        try {
            await defaultWholeBufferGate(file, slug, opts);
        } catch (eD) {
            if (eD && eD.name === 'MemoryBudgetError') {
                setLastError(0, file, eD);
                throw eD;
            }
        }
    }

    var tier1Err = null, tier2Err = null, tier15Tried = false;

    // Test seams (Playwright: audio-decoder-adaptive-timeouts.mjs). Each
    // flag skips one tier so a single mechanism can be exercised in
    // isolation on a real file. No production caller sets these.
    var skipT1  = typeof window !== 'undefined' && !!window.__adrSkipTier1;
    var skipT12 = typeof window !== 'undefined' && !!window.__adrSkipTier12;
    var skipT15 = typeof window !== 'undefined' && !!window.__adrSkipTier15;
    var skipT2  = typeof window !== 'undefined' && !!window.__adrSkipTier2;
    var skipT3  = typeof window !== 'undefined' && !!window.__adrSkipTier3;

    /* ── The verdict is earned by DOING; the header is a WITNESS, not a judge ──
       A tier that "succeeds" can still hand back the wrong audio: Yandex
       Browser 26.8's decodeAudioData returns an HE-AAC v2 stream at TWICE its
       length (an octave down, folded to mono) and never throws — that buffer
       became a 113 s soundtrack on a 56 s video (support 2026-09-01). For the
       mp4 family the container states the duration in ONE authoritative field
       (mdhd), so there a decode that disagrees by more than 20 % is a FAILED
       tier and the chain moves on. 20 % is far above priming/padding (≤ 0.2 s)
       and far below the 2× this catches.

       ⚠️ Everywhere else the gate was REMOVED (16.09.2026), because it was
       rejecting files the decoders had read correctly. Measured over 60 days on
       RU: 182 mp3 sessions were refused this way, and in 167 of them TWO OR
       THREE independent decoders (native, WebCodecs, FFmpeg WASM) returned the
       SAME duration while the header claimed another — in both directions
       (551.9 s decoded against 822.9 s claimed; 127.6 against 71.6; 67.0
       against 526.6). An mp3 header is an estimate that lies routinely: a false
       frame sync inside the album art, a Xing frame count kept from before two
       files were glued, VBR with no Xing frame at all. The same gate fired
       across the audio side of the site — /trim 41 sessions, /vocal-remover 18,
       /bpm-key-finder 16, /audio-to-notes 15, /speed 15, /volume 15,
       /audio-to-video 11, /repair-audio 11 — and every one of them ended in
       «Не удалось декодировать аудио. Попробуйте другой файл.» on a file that
       had in fact been decoded. A disagreement outside the mp4 family is now
       recorded (`gated: 0`) and the audio is accepted. */
    var hdrInfo = null;
    try { hdrInfo = await headerAudioInfo(file); } catch (_) { hdrInfo = null; }
    var oracleSec = (hdrInfo && hdrInfo.durSec > 1) ? hdrInfo.durSec : 0;
    /* A movie with several sound traks: every tier below decodes ONE of them.
       Counted, not acted on — see soundTraks in mp4AudioInfo for why. */
    if (hdrInfo && hdrInfo.soundTracks > 1) {
        try { window.__ae && window.__ae('feature_use', {
            slug: slug || '', f: 'multi_sound_tracks', n: hdrInfo.soundTracks,
            codecs: (hdrInfo.soundCodecs || []).join('+'), ch: hdrInfo.channels || 0, ext: getExt(file),
        }); } catch (_) {}
    }
    /* mp4 family only — the container with one authoritative duration field and
       the only place a browser decoder is known to lie about length. */
    var headerIsJudge = /\.(m4a|m4b|m4r|mp4|mov|3gp2?|3gpp?)(\W|$)/i.test((file && file.name) || '')
        || /mp4|m4a|quicktime/i.test((file && file.type) || '');
    /* The two numbers ride on the error a gated disagreement throws
       (lengthError): a caller that is only asking "can the browser read
       this?" — /repair-audio's waveform — reads a header that promises 2:00
       and a decode that stops at 0:23 as damage inside the file, which is what
       it is (2026-10-30: stick-damaged m4a/flac/ogg were called healthy). */
    var lastDisagree = null;
    function lengthError(msg) {
        var e = new Error(msg);
        if (lastDisagree) { e.decodedSec = lastDisagree.got; e.declaredSec = lastDisagree.expected; }
        return e;
    }
    function lengthDisagrees(buf, tier) {
        if (!oracleSec || !buf || !(buf.duration > 0)) return false;
        var ratio = buf.duration / oracleSec;
        if (ratio > 0.8 && ratio < 1.2) return false;
        lastDisagree = { got: buf.duration, expected: oracleSec };
        try { window.__ae && window.__ae('feature_use', {
            f: 'decode_duration_mismatch', tier: tier, expected: +oracleSec.toFixed(2),
            got: +buf.duration.toFixed(2), sr: buf.sampleRate, ch: buf.numberOfChannels,
            method: hdrInfo.method || '', ext: getExt(file),
            gated: headerIsJudge ? 1 : 0
        }); } catch (_) {}
        return headerIsJudge;
    }

    // Tier 1.5 (when preferred-first) — WebCodecs streaming decode runs BEFORE
    // native tier 1. Used for formats where decodeAudioData is known to stall
    // on size — long mp3 on Yandex/Chromium (whole-buffer allocation hangs past
    // our 30 s timeout, then tier 2 FFmpeg WASM takes 2-3 min single-thread,
    // by which point users abandon). WebCodecs Mp3DecoderStream decodes 43 MB
    // in seconds. On failure we skip tier 1 (it would stall the same way) and
    // go directly to tier 2. See public/js/webcodecs-audio-decode.js.
    var preferTier15First = !skipT15 && window.WebCodecsAudioDecode &&
                            window.WebCodecsAudioDecode.supported(file) &&
                            typeof window.WebCodecsAudioDecode.prefersFirst === 'function' &&
                            window.WebCodecsAudioDecode.prefersFirst(file);
    // Whether native still gets its turn after a preferred-first failure:
    // mp4-family yes (WebCodecs refuses codecs native may decode), big mp3 no
    // (native is what stalls there). Owned by webcodecs-audio-decode.js.
    var nativeAfterT15 = preferTier15First
        && typeof window.WebCodecsAudioDecode.nativeAfterFailure === 'function'
        && window.WebCodecsAudioDecode.nativeAfterFailure(file);
    if (preferTier15First) {
        tier15Tried = true;
        try {
            if (signal && signal.aborted) throw _abortedByUser();
            if (opts.onProgress) try { opts.onProgress(0.1); } catch(_){}
            var bW0 = await withTimeout(tier15Decode(file, ctx), tier15TimeoutMs(file), 'tier1.5 decode (prefersFirst)');
            if (bW0 && bW0.length && lengthDisagrees(bW0, 1.5)) throw lengthError('tier1.5 length disagrees with container');
            if (bW0 && bW0.length) {
                emitRecovered(1.5, slug, Date.now() - t0);
                if (opts.onProgress) try { opts.onProgress(1.0); } catch(_){}
                return { buffer: bW0, tier: 1.5, ms: Date.now() - t0 };
            }
        } catch (eW0) {
            emitTierError(1.5, slug, file, eW0);
        }
    }

    // Tier 1 (skipped when preferTier15First was true for a big mp3 —
    // re-running native decodeAudioData on a file we KNEW would stall just
    // burns the 30 s budget a second time. Going straight to FFmpeg / recover
    // is faster. mp4-family DOES fall back here — see nativeAfterT15.)
    if ((!preferTier15First || nativeAfterT15) && !skipT1) {
        try {
            if (opts.onProgress) try { opts.onProgress(0.05); } catch(_){}
            var b1 = await tier1Native(file, ctx, signal, hdrInfo);
            if (lengthDisagrees(b1, 1)) throw lengthError('decodeAudioData length disagrees with container');
            if (opts.onProgress) try { opts.onProgress(1.0); } catch(_){}
            return { buffer: b1, tier: 1, ms: Date.now() - t0 };
        } catch (e1) {
            tier1Err = e1;
            if (isFatal(e1)) {
                setLastError(1, file, e1);
                throw e1;
            }
            // The door in timbrica-core.js read the moov: tracks, none of them
            // sound. That is the tier-0 answer arriving late (its probe can
            // miss on a cold page) — FFmpeg cannot extract a track that is not
            // there, so the 30 MB dive below would only add a wait.
            if (e1 && e1.noAudioTrack) {
                var eNo = noAudioStreamError(file, 'moov');
                setLastError(1, file, eNo);
                throw eNo;
            }
            emitTierError(1, slug, file, e1);
        }
    }

    // Tier 1.2 — a PCM WAV the native decoder refused. There is nothing to
    // DECODE in one: it is a header and raw samples. Chromium's decodeAudioData
    // still refuses valid ones over a channel mask it does not map (5.1 back
    // 0x3F — the default FFmpeg, Audition and most DAWs write), and before this
    // tier the only rescue was FFmpeg, which writes 0x3F again, so the file
    // failed every tier after 3+ s and a 30 MB core download. Parsing it here
    // needs neither, and keeps the file's own bit depth and rate.
    if (tier1Err && !skipT1 && !skipT12) {
        try {
            var head12 = file.slice ? new Uint8Array(await file.slice(0, 12).arrayBuffer()) : null;
            var isWav = head12 && head12[0] === 0x52 && head12[1] === 0x49 && head12[2] === 0x46 && head12[3] === 0x46
                && head12[8] === 0x57 && head12[9] === 0x41 && head12[10] === 0x56 && head12[11] === 0x45;
            if (isWav) {
                if (signal && signal.aborted) throw _abortedByUser();
                var abWav = await fileToArrayBuffer(file);
                if (abWav instanceof Uint8Array) abWav = abWav.buffer.slice(abWav.byteOffset, abWav.byteOffset + abWav.byteLength);
                var bWav = wavPcmToAudioBuffer(abWav, ctx);
                if (bWav && bWav.length && !lengthDisagrees(bWav, 1.2)) {
                    emitRecovered(1.2, slug, Date.now() - t0);
                    if (opts.onProgress) try { opts.onProgress(1.0); } catch(_){}
                    return { buffer: bWav, tier: 1.2, ms: Date.now() - t0 };
                }
            }
        } catch (eWav) {
            emitTierError(1.2, slug, file, eWav);
        }
    }

    // Tier 1.5 (post-tier-1, for files NOT preferred-first) — WebCodecs +
    // MP4Box demux (native AudioDecoder, no 30 MB FFmpeg WASM, no
    // SharedArrayBuffer/COEP). Rescues AAC-in-MP4-family (m4a/m4b/mp4/mov/aac)
    // that native decodeAudioData fails to demux — the single biggest
    // decode-failure cohort (mov on iOS Safari / Yandex). Proven on a real
    // iPhone .mov (native "Decoding failed" → WebCodecs decoded 18.5 s) and
    // RMS-equal to native on m4a/mp4/mov/fragmented/stereo/long fixtures.
    // Runs BEFORE the FFmpeg tier so the common case never pays the 30 MB
    // WASM dive. Fully guarded — supported() gates on mp4-family + mp3 +
    // AudioDecoder feature-detect, and ANY failure falls through to Tier 2
    // exactly as before, so it can only improve decode, never regress.
    if (!tier15Tried && !skipT15 && window.WebCodecsAudioDecode && window.WebCodecsAudioDecode.supported(file)) {
        try {
            if (signal && signal.aborted) throw _abortedByUser();
            if (opts.onProgress) try { opts.onProgress(0.25); } catch(_){}
            var bW = await withTimeout(tier15Decode(file, ctx), tier15TimeoutMs(file), 'tier1.5 decode');
            if (bW && bW.length && lengthDisagrees(bW, 1.5)) throw lengthError('tier1.5 length disagrees with container');
            if (bW && bW.length) {
                // tier: 1.5 — numeric so existing rollups/assertions that
                // compare r.tier with === keep working (sortable too, sits
                // between 1=native and 2=ffmpeg in dashboards).
                emitRecovered(1.5, slug, Date.now() - t0);
                if (opts.onProgress) try { opts.onProgress(1.0); } catch(_){}
                return { buffer: bW, tier: 1.5, ms: Date.now() - t0 };
            }
        } catch (eW) {
            emitTierError(1.5, slug, file, eW);
        }
    }

    /* ── Browser decoders only (opts.browserOnly, 2026-10-30) ───────────────
       A decode that is decoration or a second opinion must not take the one
       FFmpeg engine of the page, nor run the rescue tier. /repair-audio was the
       measured case: its input waveform fell through to tier 2 while the repair
       ladder ran on the same engine (55 sessions in 60 days, 926 reinits
       skipped as "busy" — the repair stalled behind a picture), and its
       post-ladder bridge re-ran the full chain on the file FFmpeg had just
       failed: 143 attempts, 0 successes. Past the browser's own decoders the
       caller already has — or is — the FFmpeg answer. */
    if (opts.browserOnly) {
        var eB = tier1Err || new Error('the browser could not decode this file');
        try { eB.tier = 'browser'; eB.browserOnly = true; eB.alreadyReported = true; } catch (_) {}
        setLastError(1.5, file, eB);
        throw eB;
    }

    // Tier 2
    if (skipT2) { tier2Err = tier2Err || new Error('tier2 skipped (test seam)'); }
    else try {
        if (opts.onProgress) try { opts.onProgress(0.4); } catch(_){}
        var b2 = await tier2Ffmpeg(file, ctx, signal, opts);
        if (lengthDisagrees(b2, 2)) throw lengthError('tier2 length disagrees with container');
        emitRecovered(2, slug, Date.now() - t0);
        if (opts.onProgress) try { opts.onProgress(1.0); } catch(_){}
        return { buffer: b2, tier: 2, ms: Date.now() - t0 };
    } catch (e2) {
        tier2Err = e2;
        if (isFatal(e2)) {
            setLastError(2, file, e2);
            throw e2;
        }
        // FFmpeg WASM panicked / was terminated mid-call (concurrent flow
        // killed the instance, lib hit fatal codec, internal _exit). The
        // error message "called FFmpeg.terminate()" / "Aborted()" gets
        // reclassified as a "cancelled" feature_use event by
        // __aeToolError's terminate-routing — masking real Tier 2 failures.
        // Surfaced 2026-05-29 sid 1d738efb (Yandex Android, 18 MB MOV):
        // user got stuck, no Tier 2 error logged. Etalon: reinit + retry
        // once. If the original failure was transient, user is rescued;
        // if it was a real codec issue, the retry's error is logged with
        // a real message (not "cancelled") so the dashboard sees the truth.
        var _e2msg = (e2 && e2.message) || '';
        if (!opts._tier2Retried && /terminate\(\)|Aborted\(\)|FFmpeg\.terminate/i.test(_e2msg)) {
            try {
                if (typeof FFmpegProcessor !== 'undefined' && typeof FFmpegProcessor.reinit === 'function') {
                    await FFmpegProcessor.reinit();
                }
                var _retryOpts = {};
                for (var _k in opts) { if (opts.hasOwnProperty(_k)) _retryOpts[_k] = opts[_k]; }
                _retryOpts._tier2Retried = 1;
                var b2r = await tier2Ffmpeg(file, ctx, signal, _retryOpts);
                emitRecovered(2, slug, Date.now() - t0);
                if (opts.onProgress) try { opts.onProgress(1.0); } catch(_){}
                return { buffer: b2r, tier: 2, ms: Date.now() - t0 };
            } catch (e2r) {
                tier2Err = e2r;
                if (isFatal(e2r)) { setLastError(2, file, e2r); throw e2r; }
                emitTierError(2, slug, file, e2r);
            }
        } else {
            emitTierError(2, slug, file, e2);
        }
    }

    // Tier 3
    if (skipT3) {
        var eSkip = tier1Err || tier2Err || new Error('all tiers skipped (test seam)');
        try { eSkip.alreadyReported = true; } catch (_) {}
        setLastError(3, file, eSkip);
        throw eSkip;
    }
    try {
        if (opts.onProgress) try { opts.onProgress(0.7); } catch(_){}
        var b3 = await tier3Recover(file, ctx, signal, opts);
        emitRecovered(3, slug, Date.now() - t0);
        if (opts.onProgress) try { opts.onProgress(1.0); } catch(_){}
        return { buffer: b3, tier: 3, ms: Date.now() - t0 };
    } catch (e3) {
        emitTierError(3, slug, file, e3);
        // All three tiers failed. The user-facing error is the original
        // Tier 1 error (most actionable: "your browser couldn't decode this");
        // attach the chain on .cause so handlers that want it can dig.
        var finalErr = tier1Err || e3;
        try {
            finalErr.tier = 'all';
            finalErr.tier1 = tier1Err;
            finalErr.tier2 = tier2Err;
            finalErr.tier3 = e3;
            // Three typed decode_tier_failed events have already fired (tier 1, 2,
            // 3) plus any audio_recover_failed inside tier 3 — the caller's
            // outer catch must not double-count this as toast_error. The
            // err.alreadyReported gate in timbrica-core.js's __handleError
            // suppresses the duplicate while keeping the visual toast.
            finalErr.alreadyReported = true;
        } catch (_) {}
        setLastError(3, file, finalErr);
        /* ⚠️ ОДНО СОБЫТИЕ НА ПОТЕРЯННЫЙ ФАЙЛ — единственное, по которому можно
           сосчитать людей, а не ступеньки.
           `decode_tier_failed` пишется на КАЖДЫЙ неудавшийся тир, и это сделано
           намеренно (диагностика цела). Но тот, кто считает `tool_error`, видит
           ступеньки лестницы и принимает их за провалы. Замер на проде за 60
           дней: 1513 строк `decode_tier_failed` = 817 попыток, из которых
           418 (51.2%) спас следующий тир и лишь 399 провалились по-настоящему.
           Завышение в 3.8 раза — и именно по нему я сам начал это
           расследование, прочитав «11.3% сессий тула получили ошибку».
           Ступенька, названная исходом, заводит расследование не о том. */
        try {
            if (window.__ae) window.__ae('tool_error', {
                slug: slug || '',
                error_type: 'decode_failed_final',
                tiers_tried: (tier1Err ? '1' : '') + (tier2Err ? '2' : '') + '3',
                err_msg: ((finalErr && finalErr.message) || '').substring(0, 200),
                file_type: (file && file.type) || '',
                file_size: (file && file.size) || 0,
                br: browserKey(),
                _no_auto_stage: 0,
            });
        } catch (_) {}
        throw finalErr;
    }
}

// IMPORTANT: do NOT name this `window.AudioDecoder` — the browser's native
// WebCodecs API defines that as a constructor class. Until 2026-05-22 we
// were overwriting it, which broke `mp3-decoder-streaming.js`'s `new
// window.AudioDecoder(...)` callsite. Result: ~600 audio-enhancer pipeline
// failures/day with `window.AudioDecoder is not a constructor` (Sentry-free
// because the error was caught and reported only as analytics `*_failed`).
// Now `window.AudioDecoder` stays the native WebCodecs class; our shim lives
// under `window.AudioDecoderRobust` instead. The two coexist cleanly.
// ── Bounded-head decode for long recordings (2026-07-11, denoise TECNO
// 97-min dictaphone m4a, sid 52e5241f) ──────────────────────────────────────
// decodeAudioData expands compressed audio ~11× (128 kbps → 48 kHz float32):
// a 97-minute recording becomes a >1 GB AudioBuffer and the renderer is
// OOM-killed with NO JS error — the tab just dies and queued telemetry dies
// with it. Every robust() tier decodes the WHOLE file (the module header
// says as much: tiers rescue corrupt input, not long input) — so tools that
// only need the leading seconds (analysis panels, previews, waveforms) had
// no safe primitive and 86 tool blades call decodeAudioData raw.
//
// decodeHead(file, seconds, ctx, opts) reuses the hardened tier-2 machinery
// (-err_detect ignore_err, probe caps, process timeout, direct PCM-WAV →
// AudioBuffer parse with no second decodeAudioData) plus `-t N`, which stops
// the FFmpeg decoder after N seconds of output — memory stays bounded for
// any input duration. Estimate + budget are the decision helpers: callers
// compare estimateDecodedMB() against decodeBudgetMB() and take decodeHead
// when a whole-file decode would blow the tab.
function decodeBudgetMB() {
    // The `|| 8` this line used to carry was the single most optimistic
    // resolution of "deviceMemory is absent" in the codebase, and WebKit is the
    // only place it ever fired: an iPhone got the 384 MB high-tier budget — the
    // same one a 32 GB workstation gets — while its tab is killed past ~600 MB.
    // window.__memBudget owns the answer now (2 GB on iOS-WebKit → 192 MB here).
    var gb = (typeof window !== 'undefined' && window.__memBudget && window.__memBudget.gb)
        ? window.__memBudget.gb()
        : (navigator.deviceMemory || 8);
    return gb <= 4 ? 192 : 384;
}
function estimateDecodedMB(fileOrBlob, durSec) {
    // Duration-first: exact math for the 48 kHz stereo float32 the decode
    // produces. Callers should pass a real duration when they have one
    // (media-element metadata) — the size ratios below are worst-case
    // fallbacks and overestimate on purpose (the bounded path is safe for
    // any file; an underestimate risks the renderer kill).
    if (durSec > 0) return durSec * 48000 * 2 * 4 / 1048576;
    var sz = (fileOrBlob && fileOrBlob.size) || 0;
    var name = (fileOrBlob && fileOrBlob.name) || '';
    var mime = (fileOrBlob && fileOrBlob.type) || '';
    if (/\.(wav|aiff?|flac)(\W|$)/i.test(name) || /wav|aiff|flac/i.test(mime)) return sz * 2.5 / 1048576;
    // Voice codecs go down to ~24 kbps — an 8.8 MB opus is 55 minutes
    // (2.5 GB decoded). ×40 assumes ~26 kbps so low-bitrate voice notes
    // are never under-estimated into the whole-decode path.
    if (/\.(ogg|opus|webm|amr|3gp[p2]?|spx)(\W|$)/i.test(name) || /ogg|opus|webm|amr|speex/i.test(mime)) return sz * 40 / 1048576;
    return sz * 11 / 1048576;
}
async function decodeHead(file, seconds, ctx, opts) {
    opts = opts || {};
    if (!ctx) {
        var AC = window.AudioContext || window.webkitAudioContext;
        if (!AC) throw new Error('AudioContext unavailable');
        ctx = new AC();
    }
    return await tier2Ffmpeg(file, ctx, opts.signal, {
        onProgress: opts.onProgress,
        headSeconds: Math.max(1, Math.round(seconds) || 1)
    });
}

/**
 * decodeWithin(file, opts) — the DECISION, not just the helpers.
 *
 * estimateDecodedMB() and decodeBudgetMB() have existed since v1.3.0, but
 * they are two loose numbers and each caller had to remember to compare them.
 * /denoise remembered, in six places; /audio-enhancer never did, in four —
 * and that asymmetry is the whole bug report of 2026-09-07 (Премиум #6590,
 * a 42-minute 39 MB lecture on an iPhone). Measured on that exact file:
 * the spectrogram panel decoded the source AND the result whole, peaking at
 * 6031 MB of browser working set one second after the result appeared —
 * against 1038 MB for the same code on a 3-minute file. The panel's only
 * guard read FILE SIZE (100 MB) and sat AFTER the decode.
 *
 * So the comparison lives here now, once, and a caller cannot forget it:
 *
 *   { buffer, bounded, skipped, estMB, budgetMB, headSeconds }
 *
 *   under budget            → whole decode, exactly as before  (bounded=false)
 *   over budget + head > 0  → only the leading `headSeconds`   (bounded=true)
 *   over budget + no head   → nothing decoded                  (skipped=true)
 *
 * `bounded` is not decoration: a caller that shows a NUMBER about the whole
 * file (integrated LUFS, total duration) must not compute it from a head —
 * it has to skip or relabel. A caller drawing a picture may use the head.
 *
 * ⚠️ Pass `durSec` whenever the tool knows it. Without it the estimate falls
 * back to size ratios, and size is exactly the proxy that failed here: at
 * 128 kbps a 39 MB file is 42 minutes (899 MB decoded), at 320 kbps it is
 * 17 minutes. Bytes do not tell you what a decode costs; seconds do.
 */
async function decodeWithin(file, opts) {
    opts = opts || {};
    var budgetMB = decodeBudgetMB();
    var estMB = estimateDecodedMB(file, opts.durSec || 0);
    var head = Math.round(opts.headSeconds || 0);
    var out = { buffer: null, bounded: false, skipped: false,
                estMB: Math.round(estMB), budgetMB: budgetMB, headSeconds: 0 };
    if (estMB <= budgetMB) {
        var ctx = opts.ctx;
        if (!ctx) {
            var AC = window.AudioContext || window.webkitAudioContext;
            if (!AC) throw new Error('AudioContext unavailable');
            ctx = new AC();
        }
        var ab = (file && typeof file.arrayBuffer === 'function')
            ? await file.arrayBuffer() : file;
        out.buffer = await ctx.decodeAudioData(ab);
        return out;
    }
    if (head <= 0) { out.skipped = true; return out; }
    out.bounded = true;
    out.headSeconds = head;
    out.buffer = await decodeHead(file, head, opts.ctx, { signal: opts.signal });
    return out;
}

/* ── probeDuration(file) — how long is it, BEFORE any decode ─────────────
 * The one question a whole-file tool has to answer before it allocates
 * duration × rate × channels × 4 bytes. Header parsers first (FLAC / WAV /
 * Ogg / MP3 / MP4 — including a moov at the tail), then the media element's
 * metadata for a container we do not parse (a blob URL, preload=metadata,
 * bounded), then null = unknown: the caller must then fall back to the
 * conservative byte estimate, never to «fits».
 */
/* A container that does not STORE its length — WebM from MediaRecorder (our
 * own voice-over recorder among them, and every «online voice recorder» that
 * names its WebM «.wav»), FLAC written to a pipe, a live-style Ogg — reaches
 * `loadedmetadata` with `duration === Infinity` in every Chromium browser
 * (Chrome, Yandex, Edge, Opera, Android; Firefox scans and answers itself).
 * Infinity is «not stored», not «endless»: asking the element for a time past
 * the end makes Chromium walk to the last packet and publish the real length
 * in `durationchange` — the same thing a player's seek bar does. Measured
 * 29.09.2026, Chrome 154: a 40 s WebM → 40.001 s in 0.3–2 s, a 1 h WebM
 * (50 MB) → 3600.001 s in 0.55 s, a 10-min piped FLAC → 599.98 s in 0.2 s.
 * Until then this tier answered 0 («unknown») for exactly these files, and
 * /slideshow-maker, reading `<audio>.duration` itself, built an ENDLESS show on
 * them: the export sat at «Кодирование видео… 2 %» for hours (support chat
 * 655, a paying customer, 28–29.09). */
var ELEMENT_SEEK_MS = 12000;
function mediaElementDurationSec(file) {
    return new Promise(function (resolve) {
        var url = null, el = null, done = false, seeking = false, t1 = null, t2 = null;
        function finish(sec) {
            if (done) return;
            done = true;
            clearTimeout(t1); clearTimeout(t2);
            try { if (el) { el.removeAttribute('src'); el.load(); } } catch (_) {}
            try { if (url) URL.revokeObjectURL(url); } catch (_) {}
            var ok = sec > 0 && isFinite(sec);
            mediaElementDurationSec.last = ok ? (seeking ? 'seek' : 'meta') : 'none';
            resolve(ok ? sec : 0);
        }
        function known() { return el && isFinite(el.duration) && el.duration > 0; }
        try {
            if (typeof document === 'undefined' || typeof URL === 'undefined' || !URL.createObjectURL) return finish(0);
            url = URL.createObjectURL(file);
            el = document.createElement('audio');
            el.preload = 'metadata';
            el.muted = true;
            el.addEventListener('loadedmetadata', function () {
                if (known()) { finish(el.duration); return; }
                if (el.duration !== Infinity) { finish(0); return; }   // NaN: nothing to walk
                seeking = true;
                clearTimeout(t1);
                t2 = setTimeout(function () { finish(known() ? el.duration : 0); }, ELEMENT_SEEK_MS);
                try { el.currentTime = 1e101; } catch (_) { finish(0); }
            });
            // Both: some builds publish the length on durationchange, others
            // only once the seek settles.
            el.addEventListener('durationchange', function () { if (seeking && known()) finish(el.duration); });
            el.addEventListener('seeked', function () { if (seeking) finish(known() ? el.duration : 0); });
            el.addEventListener('error', function () { finish(0); });
            t1 = setTimeout(function () { finish(0); }, 8000);
            el.src = url;
        } catch (_) { finish(0); }
    });
}
async function probeDuration(file) {
    if (!file) return null;
    var info = null;
    try { info = await headerAudioInfo(file); } catch (_) { info = null; }
    if (info && info.durSec > 0) return info;
    var sec = await mediaElementDurationSec(file);
    if (sec > 0) {
        return { durSec: sec, channels: (info && info.channels) || 0, sampleRate: (info && info.sampleRate) || 0,
                 method: mediaElementDurationSec.last === 'seek' ? 'media-element-seek' : 'media-element', exact: false };
    }
    return info;   // rate/channels may still be known; the duration is not
}

/* ── wholeDecodeCeilS(file, info, opts) — the longest recording THIS device
 * can hold whole for THIS tool, in seconds. Memory, not tier: memBudgetMB()
 * (700 MB on an iOS tab, RAM-derived elsewhere) minus two copies of the
 * encoded bytes, divided by what one second costs decoded (rate × channels
 * × 4) times the tool's working-set multiplier — how many full-length
 * buffers the tool holds at its peak (opts.multiplier: a number, or a
 * function of {channels, sampleRate}; default 2.5 = the tier-0 probe's).
 * opts.maxS clamps it further (a browser limit, a measured comfort ceiling).
 * Always ≥ 1: a 0 would read as «no ceiling» in checkDuration.
 */
/* ── What the DECODER hands back, not what the header names (01.10.2026) ──
 * Sentry JS on /volume, second time: a 265 MB m4a audiobook, 6 h 21 min, passed
 * the gate (`decode_gate` dur_s 22844 < ceil_s 34945) and the whole decode died
 * after ten minutes with «Array buffer allocation failed». Two things the
 * budget did not know:
 *  - AAC with a core rate <= 24 kHz may carry SBR, signalled implicitly: the
 *    mp4 header and the codec string name the CORE rate and cannot tell (see
 *    docs/why/primitives/audio-decode-robust.md), the decoder outputs TWICE the
 *    rate, and HE-AAC v2 (parametric stereo) turns a «mono» header into
 *    stereo. Such a track is budgeted at 2× the rate and at least 2 channels.
 *  - one channel of a decoded track is ONE typed array, and no JS buffer is
 *    larger than 2 GiB: past that a whole decode cannot exist at any amount of
 *    memory — this file needs ~4 GiB per channel at 44.1 kHz. */
var CHANNEL_BUFFER_MAX_BYTES = 2 * 1024 * 1024 * 1024 - 64 * 1024 * 1024;
function decodedShape(info) {
    var rate = (info && info.sampleRate > 0) ? info.sampleRate : 48000;
    var ch = Math.min(Math.max((info && info.channels) || 2, 1), 8);
    if (info && /^mp4a$/i.test(String(info.codec || '')) && rate <= 24000) {
        rate *= 2;
        ch = Math.max(ch, 2);
    }
    return { rate: rate, ch: ch };
}

function wholeDecodeCeilS(file, info, opts) {
    opts = opts || {};
    var shape = decodedShape(info);
    var rate = shape.rate, ch = shape.ch;
    var mult = typeof opts.multiplier === 'function'
        ? opts.multiplier({ channels: ch, sampleRate: rate })
        : (opts.multiplier > 0 ? opts.multiplier : 2.5);
    if (!(mult > 0)) mult = 2.5;
    var perSecMB = rate * ch * 4 / 1048576;
    var encodedMB = encodedHeldMB(file);
    var room = memBudgetMB() - encodedMB;
    var ceil = room > 0 ? Math.floor(room / (perSecMB * mult)) : 0;
    ceil = Math.min(ceil, Math.floor(CHANNEL_BUFFER_MAX_BYTES / 4 / rate));
    if (opts.maxS > 0) ceil = Math.min(ceil, Math.floor(opts.maxS));
    return Math.max(1, ceil);
}

/* ── defaultWholeBufferGate(file, slug, opts) — what robust() does for a caller
 * that never opted into anything. Same instruments as decodeGated (probe →
 * wholeDecodeCeilS → the shared dialog), narrower contract: it only ever
 * STOPS a decode that cannot fit — no phone advisory, no split/server offers,
 * those need a tool that knows what to do with the answer. Unknown length
 * fails open, as the opt-in tier-0 probe does: a guessed «too long» on an odd
 * container would refuse files that decode fine today.
 */
async function defaultWholeBufferGate(file, slug, opts) {
    var info = null;
    try { info = await probeDuration(file); } catch (_) { info = null; }
    if (!info || !(info.durSec > 0)) return;
    var ceilS = wholeDecodeCeilS(file, info, { multiplier: opts.multiplier, maxS: opts.maxS });
    if (info.durSec <= ceilS) return;
    try {
        if (typeof window !== 'undefined' && window.__ae) window.__ae('feature_use', {
            slug: slug, f: 'decode_gate', via: 'default', dur_s: Math.round(info.durSec), ceil_s: ceilS,
            known: 1, method: info.method || 'header', over: 1,
            file_mb: Math.round(((file && file.size) || 0) / 1048576)
        });
    } catch (_) {}
    var TL = (typeof window !== 'undefined') ? window.__toolLimit : null;
    var informed = false;
    if (TL && typeof TL.checkDuration === 'function') {
        try {
            // The dialog names the device ceiling in the page's language and
            // offers what a plan can lift; `true` never comes back for a file
            // above the device ceiling, but the contract is honoured anyway.
            // file: lets the gate cut the recording into parts on this page
            // instead of sending the person to another tool (audio-parts.js).
            var verdict = await TL.checkDuration(slug, info.durSec, { deviceCeilS: ceilS, file: file });
            if (verdict === true) return;
            informed = true;
        } catch (_) { informed = false; }
    }
    var rate = (info.sampleRate > 0) ? info.sampleRate : 48000;
    var ch = Math.min(Math.max(info.channels || 2, 1), 8);
    var err = new Error('Decoded audio would not fit device memory: ~' + Math.round(info.durSec / 60) +
        ' min against a ceiling of ~' + Math.round(ceilS / 60) + ' min on this device');
    err.name = 'MemoryBudgetError';
    err.reason = 'memory_budget';
    err.expected = true;          // device constraint, not a code bug (Sentry)
    err.alreadyReported = true;   // decode_gate above
    err.userInformed = informed;  // the shared dialog has spoken; a caller's toast is a repeat
    err.durSec = Math.round(info.durSec);
    err.ceilS = ceilS;
    err.budgetMB = memBudgetMB();
    err.needMB = Math.round(info.durSec * rate * ch * 4 / 1048576);
    throw err;
}

/* ── decodeGated(file, opts) — the front door for a tool that needs the WHOLE
 * buffer: probe → gate → decode, in that order, once, for every caller.
 *
 *   { buffer, info, durSec, ceilS }              decoded (the robust ladder)
 *   { buffer: null, refused: 'gate'|'server' }   the shared gate stopped it — the
 *                                                dialog has already run; the tool
 *                                                only drops the file (or, on
 *                                                'server', starts its server lane)
 *   throws                                       decode errors, as robust() does
 *
 * The gate is __toolLimit.checkDuration (tier wall, device ceiling, phone
 * advisory) fed with the duration just probed and the device ceiling from
 * wholeDecodeCeilS; a page without the limit primitive gets the typed
 * MemoryBudgetError instead — the verdict /music-mastering already handles.
 * Why here and not in each blade: the previous shape was «decode first, look
 * at buffer.duration second» in 69 blades, and /remove-reverb — which DID
 * call checkDuration — called it after the decode too. The probe is what
 * makes the order possible, and the order is the whole fix.
 *
 * opts: slug, ctx, signal, multiplier, maxS, splitUrl, serverLane, serverBound
 */
async function decodeGated(file, opts) {
    opts = opts || {};
    var slug = opts.slug || ((typeof window !== 'undefined' && window.__TOOL_SLUG) || '');
    var info = await probeDuration(file);
    var durSec = (info && info.durSec > 0) ? info.durSec : 0;
    var shape = decodedShape(info);
    var rate = shape.rate, ch = shape.ch;
    var known = durSec > 0;
    if (!known) {
        // Unknown length: price it by the conservative byte ratios (voice
        // codecs ×40), expressed in seconds at the rate we will decode at.
        durSec = estimateDecodedMB(file, 0) / (rate * ch * 4 / 1048576);
    }
    var ceilS = wholeDecodeCeilS(file, info, opts);
    try {
        if (typeof window !== 'undefined' && window.__ae) window.__ae('feature_use', {
            slug: slug, f: 'decode_gate', dur_s: Math.round(durSec), ceil_s: ceilS, known: known ? 1 : 0,
            method: (info && info.method) || 'none', over: durSec > ceilS ? 1 : 0,
            file_mb: Math.round(((file && file.size) || 0) / 1048576)
        });
    } catch (_) {}
    var TL = (typeof window !== 'undefined') ? window.__toolLimit : null;
    if (TL && typeof TL.checkDuration === 'function') {
        var verdict = await TL.checkDuration(slug, durSec, {
            deviceCeilS: ceilS, splitUrl: opts.splitUrl, serverLane: opts.serverLane || null, serverBound: !!opts.serverBound,
            file: file
        });
        if (verdict !== true) {
            return { buffer: null, refused: verdict === 'server' ? 'server' : 'gate', info: info, durSec: durSec, ceilS: ceilS };
        }
    } else if (durSec > ceilS) {
        var err = new Error('Decoded audio would not fit device memory: ~' + Math.round(durSec / 60) +
            ' min against a ceiling of ~' + Math.round(ceilS / 60) + ' min on this device');
        err.name = 'MemoryBudgetError';
        err.reason = 'memory_budget';
        err.expected = true;
        err.durSec = durSec;
        err.ceilS = ceilS;
        err.budgetMB = memBudgetMB();
        err.needMB = Math.round(durSec * rate * ch * 4 / 1048576);
        throw err;
    }
    // gated:true — the judgement above is the gate; robust() must not ask twice.
    var r = await robust(file, opts.ctx, { slug: slug, signal: opts.signal, gated: true });
    return { buffer: (r && r.buffer) ? r.buffer : r, info: info, durSec: durSec, ceilS: ceilS, tier: r && r.tier };
}

/* ── robustRange(file, ctx, {from, to}) — a time WINDOW, heap bounded by the window ──
 *
 * robust() answers «decode this file»; this answers «decode [from, to) of
 * this file», and the difference is the whole bug it was written for. A
 * tool that shows twenty seconds of a clip has no use for the clip's other
 * ten minutes — yet every caller had only the whole-file door: `arrayBuffer()`
 * + `decodeAudioData(arr.slice(0))`, i.e. the file TWICE in flight and the
 * PCM of the WHOLE track retained. /slideshow-maker walked a 12-clip / 639 MB
 * deck through that door clip by clip and the renderer was OOM-killed two
 * seconds after «Export», seven times in a row (support, Galaxy S23 / 8 GB,
 * 22.09.2026). /video-merge had hit the same wall on 10.09 (1.3 GB clip →
 * 2.6 GB in flight) and solved it locally with FFmpeg windows; the ladder
 * below is that solution made shared, with the WebCodecs rung in front so the
 * commonest file on the site — AAC in a phone mp4/mov — needs no 30 MB wasm.
 *
 *   R1  WebCodecsAudioDecode.decodeRange — mp4-family with an AudioDecoder:
 *       ftyp+moov and only the window's access units are read off the disk.
 *   R2  FFmpeg window — processStreaming (WORKERFS mount, no copy) with
 *       `-ss from -t len` → WAV → decodeAudioData. Any container or codec;
 *       heap = O(window). Costs the wasm, so it is the FIRST rung only for
 *       files that are too big to read whole.
 *   R3  robust() whole + slice — the old path, kept because for a small file
 *       (≤ RANGE_WHOLE_MAX_BYTES) native decode of the whole is faster than
 *       loading FFmpeg, and because robust() carries the recovery tiers. Its
 *       default whole-buffer memory gate still applies, so a big file whose
 *       windows all failed is refused, not OOM-killed.
 *
 *   → { buffer, tier: 'wc-range' | 'ff-range' | 'whole', ms, from, to }
 *   The buffer holds EXACTLY [from, to) seconds (silence past the track end,
 *   so a slot longer than the clip is honoured); `to` absent/Infinity = the
 *   track end. `opts.onTick()` is called at every rung boundary so a caller's
 *   stall guard sees progress, not a long silence.
 */
var RANGE_WHOLE_MAX_BYTES = 24 * 1048576;
var RANGE_TIER_TIMEOUT_MS = 300000;
// Test seam: `window.__rangeWholeMaxBytes` moves the small-file threshold so a
// guard can drive a 3 MB fixture down the window rungs.
function rangeWholeMaxBytes() { return (window.__rangeWholeMaxBytes > 0) ? window.__rangeWholeMaxBytes : RANGE_WHOLE_MAX_BYTES; }

function rangeSliceBuffer(ctx, whole, from, to) {
    var rate = whole.sampleRate, ch = whole.numberOfChannels;
    var end = (to === Infinity) ? Math.max(from, whole.duration) : to;
    var len = Math.max(1, Math.round((end - from) * rate));
    var out = ctx.createBuffer(ch, len, rate);
    var start = Math.round(from * rate);
    var avail = Math.min(len, Math.max(0, whole.length - start));
    for (var c = 0; c < ch; c++) {
        if (avail > 0) out.getChannelData(c).set(whole.getChannelData(c).subarray(start, start + avail), 0);
    }
    return out;
}

async function rangeFfmpegWindow(file, ctx, from, to, opts) {
    if (window.__ensureFFmpeg) { try { await window.__ensureFFmpeg(); } catch (_) {} }
    var hasReal = window.__hasRealFFmpeg ? window.__hasRealFFmpeg() : (typeof FFmpegProcessor !== 'undefined');
    if (!hasReal || typeof FFmpegProcessor.processStreaming !== 'function') throw new Error('FFmpegProcessor unavailable');
    if (!FFmpegProcessor.isReady()) await FFmpegProcessor.init(function () {});
    if (opts.onTick) try { opts.onTick(); } catch (_) {}
    var sr = String(Math.round(ctx.sampleRate || 44100));
    /* Окно до сэмпла: вход перематывается на ЦЕЛУЮ секунду до начала, а
       точное начало и конец режет atrim СЧЁТОМ сэмплов от этой секунды. Замер
       02.10.2026 (окна против декода того же файла от нуля): `-ss from.toFixed(3)`
       у входа сдвигал mp4-окно на округление до мс (до ±24 сэмплов — стыки окон
       и разрезы склейки щёлкали); у Matroska (метки в мс) и `-ss` до микросекунды
       у входа квантуется в мс — FFmpeg переводит смещение в шкалу потока, а
       целая секунда точна в любой шкале (1/1000, 1/44100, 1/48000, 1/90000).
       Счёт сэмплов, а не `-ss`/`-t` после `-i`: выходные `-ss`/`-t` читают
       метки времени, а `-copyts` — ГЛОБАЛ FFmpeg и переживает exec: после
       одного такого запуска на странице (точная обрезка ffmpeg-processor) окно
       с выходным `-t` выходило пустым. Этот рецепт одинаков в обоих состояниях:
       mp4 — 0 сэмплов от декода с нуля, mkv — предел меток самого контейнера.
       Разбор — docs/why/primitives/audio-decode-robust.md («Окно FFmpeg»). */
    var rate = Math.round(ctx.sampleRate || 44100);
    var seekTo = Math.max(0, Math.floor(from) - 1);
    var pre = seekTo > 0 ? ['-ss', String(seekTo)] : [];
    var n0 = Math.round((from - seekTo) * rate);
    var trim = 'atrim=start_sample=' + n0;
    if (to !== Infinity) {
        pre.push('-t', (to - seekTo + 0.5).toFixed(6));
        trim += ':end_sample=' + (n0 + Math.max(1, Math.round((to - from) * rate)));
    }
    var res = await withTimeout(FFmpegProcessor.processStreaming(
        file, ['-vn', '-sn', '-map', '0:a:0', '-ac', '2', '-af', 'aresample=' + rate + ',' + trim, '-ar', sr, '-c:a', 'pcm_s16le'], 'range.wav', undefined, pre
    ), RANGE_TIER_TIMEOUT_MS, 'range tier ffmpeg');
    try {
        if (!res || !res.blob || res.blob.size < 100) throw new Error('FFmpeg returned empty / undersized output');
        // An intermediate, not the tool's output — keep the blob auto-tracker quiet.
        try { if (typeof window.__skipBlobAutoTrack === 'function') window.__skipBlobAutoTrack(res.blob); } catch (_) {}
        var buf = await ctx.decodeAudioData(await res.blob.arrayBuffer());
        if (to !== Infinity) {
            var want = Math.max(1, Math.round((to - from) * buf.sampleRate));
            if (Math.abs(buf.length - want) > 1) buf = rangeSliceBuffer(ctx, buf, 0, to - from);
        }
        return buf;
    } finally {
        if (res && res.url) { try { URL.revokeObjectURL(res.url); } catch (_) {} }
    }
}

async function robustRange(file, ctx, opts) {
    opts = opts || {};
    var slug = opts.slug || (window.__TOOL_SLUG || '');
    var from = Math.max(0, +opts.from || 0);
    var to = (opts.to == null || !isFinite(+opts.to)) ? Infinity : Math.max(from, +opts.to);
    var t0 = Date.now();
    if (!file) throw new Error('AudioDecoderRobust.robustRange called without a file');
    ctx = ensureCtx(ctx);
    if (!ctx) throw new Error('AudioContext unavailable on this device');
    var size = (file && file.size) || 0;
    var W = window.WebCodecsAudioDecode;
    var mp4 = !!(W && typeof W.isMp4Family === 'function' && W.isMp4Family(file));
    var big = size > rangeWholeMaxBytes();
    var tried = [];
    function reason(e) { return String((e && e.message) || e || '?').slice(0, 80); }
    function finish(buf, tier) {
        try { buf._rangeTier = tier; } catch (_) {}
        return { buffer: buf, tier: tier, ms: Date.now() - t0, from: from, to: (to === Infinity) ? from + buf.duration : to, tried: tried };
    }
    function tick() { if (opts.onTick) try { opts.onTick(); } catch (_) {} }

    // R1 — the window straight from the container, no wasm.
    if (mp4 && W && typeof W.decodeRange === 'function' && typeof AudioDecoder !== 'undefined') {
        try {
            var b1 = await withTimeout(W.decodeRange(file, ctx, { from: from, to: to }), RANGE_TIER_TIMEOUT_MS, 'range tier webcodecs');
            return finish(b1, 'wc-range');
        } catch (e1) { tried.push('wc:' + reason(e1)); tick(); }
    }
    // «Is there a sound track at all?» — asked ONCE, and only when the cheap
    // rung did not answer (a phone clip with sound never pays for it). A file
    // whose container says «no sound» ends here with the typed
    // NoAudioStreamError: nothing below can decode a track that is not there.
    // Until 25.09.2026 such a file went on through the FFmpeg window and the
    // whole-file decode — a wasm run per clip, a decode ERROR in the funnel —
    // and callers told people «the browser could not read the sound» about a
    // clip that has none (/slideshow-maker: 269 of 485 «silent» clips in the
    // three days after the window ladder landed were exactly this).
    try {
        await tier0NoAudioStreamProbe(file, slug);
    } catch (e0) {
        if (e0 && e0.name === 'NoAudioStreamError') { e0.tried = tried; throw e0; }
    }
    // R2 — FFmpeg window, first for anything too big to read whole.
    if (big) {
        try { return finish(await rangeFfmpegWindow(file, ctx, from, to, opts), 'ff-range'); }
        catch (e2) { tried.push('ff:' + reason(e2)); tick(); }
    }
    // R3 — whole decode + slice: cheapest for a small file, last resort for a big one
    // (robust()'s own memory gate decides whether «whole» is affordable at all).
    try {
        // audioStreamChecked: the presence question is answered above — robust()
        // must not ask it (and report it) a second time.
        var r = await robust(file, ctx, { slug: slug, signal: opts.signal || null, audioStreamChecked: true });
        var whole = (r && r.buffer) ? r.buffer : r;
        if (!whole || !whole.length) throw new Error('empty decode');
        return finish(rangeSliceBuffer(ctx, whole, from, to), 'whole');
    } catch (e3) { tried.push('whole:' + reason(e3)); tick(); }
    if (!big) {
        try { return finish(await rangeFfmpegWindow(file, ctx, from, to, opts), 'ff-range'); }
        catch (e4) { tried.push('ff:' + reason(e4)); }
    }
    var err = new Error('robustRange: ' + tried.join(' | '));
    err.tried = tried;
    throw err;
}

window.AudioDecoderRobust = {
    robust: robust,
    robustRange: robustRange,
    decodeHead: decodeHead,
    decodeWithin: decodeWithin,
    estimateDecodedMB: estimateDecodedMB,
    decodeBudgetMB: decodeBudgetMB,
    // The pre-decode door (2026-09-12, support #6831): length first, gate
    // second, decode third. Whole-buffer tools call decodeGated and nothing
    // else; probeDuration is for a tool that needs the number for its own UI.
    probeDuration: probeDuration,
    decodeGated: decodeGated,
    defaultWholeBufferGate: defaultWholeBufferGate,
    headerAudioInfo: headerAudioInfo,
    memBudgetMB: memBudgetMB,
    // Exposed for unit tests.
    _withTimeout: withTimeout,
    _isFatal: isFatal,
    _browserKey: browserKey,
    _wavPcmToAudioBuffer: wavPcmToAudioBuffer,
    _tier1TimeoutMs: tier1TimeoutMs,
    _tier0MemoryBudgetProbe: tier0MemoryBudgetProbe,
    _wholeDecodeCeilS: wholeDecodeCeilS,
    _decodedShape: decodedShape,
    version: '1.5.1'
};

// Backwards-compatible alias for the 9 tools that already use the previous
// __decodeAudio shape. Keep until we sweep them; then this wrapper can
// shrink to just `return (await AudioDecoderRobust.robust(file, ctx)).buffer;`
// in app.blade.php.
window.__decodeAudio = async function(ctx, file) {
    var r = await robust(file, ctx);
    return r.buffer;
};

// __detectVideoCodec(file) — lightweight FourCC scan of the first 256 KB
// of a video container. Returns one of: 'hevc' | 'h264' | 'av1' | 'vp9' |
// 'vp8' | null. Heuristic-grade — looks for sample-description-entry
// (stsd) child atoms by name. For most iPhone .mov and Android .mp4
// files the moov box (and thus stsd) sits within the first 256 KB, so
// the scan is fast and accurate. When the moov is at the end of the
// file (legacy QuickTime, some screen-recordings) the scan returns null
// — caller should treat that as "unknown" rather than "no codec".
//
// Drives the user-facing CTA on /add-music-to-video when WebAudio
// decode AND HTMLMediaElement duration fallback BOTH fail: if we
// detect HEVC, point the user to a converter rather than show a
// generic "audio_load failed" toast (the 2026-05-29 /pino/streams
// audit cluster — 16 sessions / 7d, 31% abandoned). Other detected
// codecs (h264, av1) imply the failure is something else (genuinely
// corrupt file, browser quirk) and we surface the generic message.
window.__detectVideoCodec = async function(file) {
    if (!file || !(file instanceof Blob)) return null;
    // 256 KB ≫ typical iPhone moov header (~few KB), small enough to
    // not noticeably hurt a 100 MB upload. The slice() is async-cheap.
    var slice = file.slice(0, 256 * 1024);
    var buf;
    try {
        buf = await slice.arrayBuffer();
    } catch (_) {
        return null;
    }
    var bytes = new Uint8Array(buf);
    // Scan for known video FourCC strings. Order matters — HEVC is the
    // class we're rescuing, so check it first; if we found it, no need
    // to keep scanning. (FourCC strings are exactly 4 ASCII bytes.)
    var FOURCC = {
        // HEVC sample-entry atoms
        hev1: 'hevc', hvc1: 'hevc',
        // H.264 sample-entry atoms
        avc1: 'h264', avc3: 'h264',
        // AV1, VP9, VP8 sample-entry atoms
        av01: 'av1',  vp09: 'vp9',  vp08: 'vp8'
    };
    // Manual byte-compare loop — avoids constructing a 256K String and
    // running a regex (allocates ~1 MB on iOS Safari). Walks until a
    // hit; bails as soon as one of the codecs is found.
    var n = bytes.length;
    for (var i = 0; i < n - 4; i++) {
        var b0 = bytes[i], b1 = bytes[i+1], b2 = bytes[i+2], b3 = bytes[i+3];
        // Quick ASCII range filter — codec names are lowercase letters
        // + digits. Skip any 4-byte window that can't possibly match.
        if (b0 < 0x30 || b0 > 0x7A) continue;
        var fourcc = String.fromCharCode(b0, b1, b2, b3);
        if (FOURCC[fourcc]) return FOURCC[fourcc];
    }
    return null;
};

if (typeof window.__drainSharedModule === 'function') window.__drainSharedModule('AudioDecoderRobust');

})();
